import { useState, useEffect, useRef, useContext } from 'react';
import {
  ChevronRightIcon,
  DocumentArrowUpIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  PlusIcon,
  ChevronLeftIcon,
  SparklesIcon,
  XMarkIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';
import { ArrowPathIcon, ArrowUpCircleIcon } from '@heroicons/react/20/solid';
import { VictoryAxis, VictoryChart, VictoryLine, VictoryTheme } from 'victory';
import ManagerNav from './managernav';
import { AuthContext } from '../AuthContext';

const MONGO_API_BASE = (process.env.REACT_APP_MONGO_URL || 'https://analytics.insightos.co.uk').replace(/\/+$/, '');

// ------------------------------------------------------------
// DATA TYPES & HELPERS (unchanged)
// ------------------------------------------------------------
const DATA_TYPES = ['TEXT', 'INT', 'DECIMAL', 'CURRENCY', 'DATE', 'TIME', 'DATETIME', 'BOOLEAN', 'EMAIL', 'PHONE', 'URL', 'JSON',];
const FINANCIAL_DIRECTIONS = ['neutral', 'revenue', 'expense'];

const financialKeywords = [
  'cost', 'price', 'value', 'budget', 'revenue', 'spend', 'fee', 'amount', 
  'rate', 'salary', 'rent', 'margin', 'turnover', 'commission', 'valuation',
  'contractvalue', 'currentcost', 'estimatedfinalcost', 'agreedrate', 'totalcost',
  'purchasecost', 'hirecost', 'maintenancecost', 'costimpact', 'revenueimpact',
  'originalvalue', 'variationvalue', 'standardprice', 'standardcost', 'monthlyvalue',
  'monthlycost', 'sellingprice', 'ingredientcost', 'grossmargin', 'ordervalue',
  'placementfee', 'expectedrevenue', 'potentialcost', 'listingcost', 'managementfee',
  'estimatedcost', 'actualcost', 'quotedprice', 'developmentcost', 'licencecost',
  'supportcost', 'contractvalue'
];
const dateKeywords = ['date', 'deadline', 'start', 'end', 'expiry', 'review', 'delivery', 'opened', 'closed', 'submitted'];
const numberKeywords = ['quantity', 'count', 'capacity', 'duration', 'frequency', 'rate', 'percentage', 'score', 'rating'];

const toSchemaKey = (value) => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
const formatFieldLabel = (value) => String(value || '').replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const parseDateSafe = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.valueOf()) ? date : null;
};

const isZeroHoursContract = (value = '') => /^(0|0 hours?|zero hours?)$/i.test(String(value).trim());

const getMonthlyStaffCost = (staffProfiles, date) => staffProfiles.reduce((total, staff) => {
  const startDate = parseDateSafe(staff.StartDate);
  const endDate = parseDateSafe(staff.EndDate);
  if ((startDate && date < startDate) || (endDate && date > endDate)) return total;
  const weeklyHours = isZeroHoursContract(staff.ContractType) ? toNumber(staff.AverageHours) : toNumber(staff.ContractedHours);
  return total + (weeklyHours * toNumber(staff.HourlyRate) * (52 / 12));
}, 0);

const normalizeMilestoneType = (value = '') => {
  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'weekly' || normalized === 'week') return 'Weekly';
  if (normalized === 'monthly' || normalized === 'month') return 'Monthly';
  if (normalized === 'quarterly' || normalized === 'quarter') return 'Quarterly';
  if (normalized === 'yearly' || normalized === 'annual' || normalized === 'year') return 'Yearly';
  return null;
};

const normalizeSubscription = (value = '') => {
  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'weekly' || normalized === 'week') return 'weekly';
  if (normalized === 'monthly' || normalized === 'month') return 'monthly';
  if (normalized === 'annual' || normalized === 'yearly' || normalized === 'year') return 'annual';
  return '';
};

const getMonthlySubscriptionAmount = (amount, subscription) => {
  const normalized = normalizeSubscription(subscription);
  if (normalized === 'weekly') return toNumber(amount) * (52 / 12);
  if (normalized === 'monthly') return toNumber(amount);
  if (normalized === 'annual') return toNumber(amount) / 12;
  return 0;
};

const isContractActiveOn = (customer, date) => {
  if (!toNumber(customer.Amount) || !normalizeSubscription(customer.Subscription)) return false;
  const startDate = parseDateSafe(customer.ContractStart);
  const endDate = parseDateSafe(customer.ContractEnd);
  return (!startDate || date >= startDate) && (!endDate || date <= endDate);
};

const buildOperationalFinance = ({ projects, customers, staffProfiles, milestones, dataLayerSeries }) => {
  const today = new Date();
  const months = Array.from({ length: 6 }, (_, index) => new Date(today.getFullYear(), today.getMonth() - 5 + index, 1));
  const externalProjects = projects.filter((project) => project?.ProjectType === 'External');
  const schemaRevenue = new Map((dataLayerSeries.find((series) => series.key === 'revenue')?.points || []).map((point) => [point.date, toNumber(point.value)]));
  const schemaExpenses = new Map((dataLayerSeries.find((series) => series.key === 'expenditure')?.points || []).map((point) => [point.date, toNumber(point.value)]));
  const latestMilestones = milestones.reduce((result, milestone) => {
    const type = normalizeMilestoneType(milestone.Type);
    if (!type || (result[type] && new Date(result[type].CreatedAt) >= new Date(milestone.CreatedAt))) return result;
    return { ...result, [type]: milestone };
  }, {});
  const series = months.map((month) => {
    const monthKey = month.toISOString().slice(0, 7);
    const projectTotals = externalProjects.reduce((total, project) => {
      const projectDate = parseDateSafe(project.StartDate || project.created_at || project.CreatedAt);
      if (!projectDate || projectDate.toISOString().slice(0, 7) !== monthKey) return total;
      return { revenue: total.revenue + toNumber(project.Price), profit: total.profit + toNumber(project.NetProfit) };
    }, { revenue: 0, profit: 0 });
    const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    const subscriptionRevenue = customers
      .filter((customer) => isContractActiveOn(customer, monthEnd))
      .reduce((total, customer) => total + getMonthlySubscriptionAmount(customer.Amount, customer.Subscription), 0);
    const dataLayerRevenue = schemaRevenue.get(monthKey) || 0;
    const dataLayerExpenses = schemaExpenses.get(monthKey) || 0;
    const staffCost = getMonthlyStaffCost(staffProfiles, monthEnd);
    const totalRevenue = projectTotals.revenue + subscriptionRevenue + dataLayerRevenue;
    const totalCosts = staffCost + dataLayerExpenses;
    return {
      date: monthKey,
      projectRevenue: projectTotals.revenue,
      projectProfit: projectTotals.profit,
      subscriptionRevenue,
      dataLayerRevenue,
      dataLayerExpenses,
      staffCost,
      totalRevenue,
      totalCosts,
      netProfit: totalRevenue - totalCosts,
      monthlyTarget: toNumber(latestMilestones.Monthly?.Revenue),
    };
  });
  const current = series[series.length - 1];
  return {
    metrics: [
      { metricKey: 'total_revenue', label: 'Project revenue this month', total: current.totalRevenue, format: 'currency', kind: 'revenue' },
      { metricKey: 'staff_cost', label: 'Monthly staff cost', total: current.staffCost, format: 'currency', kind: 'expense' },
      { metricKey: 'total_costs', label: 'Total expenditure this month', total: current.totalCosts, format: 'currency', kind: 'expense' },
      { metricKey: 'net_profit', label: 'Net profit this month', total: current.netProfit, format: 'currency', kind: current.netProfit >= 0 ? 'profit' : 'expense' },
      { metricKey: 'monthly_milestone', label: 'Monthly revenue target', total: current.monthlyTarget, format: 'currency', kind: 'target' },
    ],
    milestones: Object.fromEntries(['Weekly', 'Monthly', 'Quarterly', 'Yearly'].map((type) => [type, toNumber(latestMilestones[type]?.Revenue)])),
    series,
  };
};

const inferReferenceTableKey = (fieldName, tableName, tableNames) => {
  const fieldKey = toSchemaKey(fieldName);
  return tableNames.find((candidate) => {
    if (candidate === tableName) return false;
    const candidateKey = toSchemaKey(candidate);
    const singularKey = candidateKey.endsWith('ies') ? `${candidateKey.slice(0, -3)}y` : candidateKey.replace(/s$/, '');
    return fieldKey === candidateKey || fieldKey === singularKey;
  }) || null;
};

const inferFinancialDirection = (field) => {
  if (FINANCIAL_DIRECTIONS.includes(field?.financialDirection)) return field.financialDirection;
  const key = toSchemaKey(field?.financialRole || field?.name);
  if (key === 'revenue' || /(revenue|selling|contract_value|amount_recovered)/.test(key)) return 'revenue';
  if (key === 'cost' || /(cost|spend|expense|budget|fee)/.test(key)) return 'expense';
  return 'neutral';
};

const withInferredReferences = (table, tables) => ({
  ...table,
  fields: table.fields.map((field) => ({
    ...field,
    referenceTableKey: field.referenceTableKey || inferReferenceTableKey(field.name, table.name, tables.map((entry) => entry.name)),
  })),
});

function inferFieldMetadata(fieldName) {
  const lower = fieldName.toLowerCase();
  let type = 'TEXT';
  let financial = false;

  if (financialKeywords.some(k => lower.includes(k))) {
    type = 'CURRENCY';
    financial = true;
  } else if (dateKeywords.some(k => lower.includes(k))) {
    type = 'DATE';
  } else if (numberKeywords.some(k => lower.includes(k))) {
    type = 'INT';
  } else if (lower.includes('email')) {
    type = 'EMAIL';
  } else if (lower.includes('phone') || lower.includes('mobile') || lower.includes('telephone')) {
    type = 'PHONE';
  } else if (lower.includes('url') || lower.includes('website') || lower.includes('link')) {
    type = 'URL';
  }

  return { type, financial };
}

// ------------------------------------------------------------
// TEMPLATE DATA (unchanged)
// ------------------------------------------------------------
// TEMPLATE DATA (18 industries) – full list
// ------------------------------------------------------------
const TEMPLATES = [
  {
    id: 'construction',
    name: 'Construction',
    icon: '🏗️',
    description: 'Sites, subcontractors, materials, equipment, variations, inspections.',
    tables: [
      {
        name: 'Sites',
        fields: ['SiteName', 'Address', 'SiteType', 'SiteManager', 'Status', 'StartDate', 'ExpectedCompletion', 'PlanningReference', 'ContractValue', 'Budget', 'CurrentCost', 'EstimatedFinalCost']
      },
      {
        name: 'Subcontractors',
        fields: ['CompanyName', 'Trade', 'ContactName', 'Telephone', 'Email', 'Accreditation', 'InsuranceExpiry', 'ComplianceStatus', 'AgreedRate', 'ContractValue', 'Status']
      },
      {
        name: 'Materials',
        fields: ['MaterialName', 'Category', 'Supplier', 'Quantity', 'UnitCost', 'TotalCost', 'RequiredDate', 'DeliveryDate', 'Status']
      },
      {
        name: 'Equipment',
        fields: ['EquipmentName', 'Type', 'SerialNumber', 'Location', 'PurchaseCost', 'HireCost', 'MaintenanceCost', 'InspectionDate', 'MaintenanceDate', 'Status']
      },
      {
        name: 'Variations',
        fields: ['Site', 'Description', 'RequestedBy', 'OriginalValue', 'VariationValue', 'CostImpact', 'RevenueImpact', 'ApprovalStatus', 'Date']
      },
      {
        name: 'Inspections',
        fields: ['Site', 'InspectionType', 'Inspector', 'Date', 'Result', 'RemedialCost', 'FollowUpDate', 'Status']
      }
    ]
  },
  {
    id: 'consumer-services',
    name: 'Consumer Services',
    icon: '🛎️',
    description: 'Services, bookings, locations, equipment, packages.',
    tables: [
      {
        name: 'Services',
        fields: ['ServiceName', 'Category', 'Description', 'StandardPrice', 'StandardCost', 'Duration', 'Margin', 'Status']
      },
      {
        name: 'Bookings',
        fields: ['Service', 'Date', 'Time', 'AssignedStaff', 'Location', 'Price', 'Cost', 'Status', 'Notes']
      },
      {
        name: 'ServiceLocations',
        fields: ['LocationName', 'Address', 'Type', 'OpeningHours', 'Capacity', 'Status']
      },
      {
        name: 'ServiceEquipment',
        fields: ['Equipment', 'Type', 'Location', 'PurchaseCost', 'MaintenanceCost', 'MaintenanceDate', 'Status']
      },
      {
        name: 'ServicePackages',
        fields: ['PackageName', 'ServicesIncluded', 'Price', 'Cost', 'Margin', 'Status']
      }
    ]
  },
  {
    id: 'events-services',
    name: 'Events Services',
    icon: '🎪',
    description: 'Events, venues, suppliers, equipment, requirements.',
    tables: [
      {
        name: 'Events',
        fields: ['EventName', 'EventType', 'Date', 'Venue', 'Capacity', 'Budget', 'Revenue', 'EstimatedCost', 'ActualCost', 'EstimatedProfit', 'Status']
      },
      {
        name: 'Venues',
        fields: ['VenueName', 'Location', 'Capacity', 'HireCost', 'Availability', 'Facilities', 'Status']
      },
      {
        name: 'Suppliers',
        fields: ['SupplierName', 'ServiceType', 'ContactName', 'Telephone', 'Email', 'Cost', 'Event', 'ContractStatus', 'Status']
      },
      {
        name: 'Equipment',
        fields: ['EquipmentName', 'Type', 'Quantity', 'HireCost', 'PurchaseCost', 'Location', 'Condition', 'Status']
      },
      {
        name: 'EventRequirements',
        fields: ['Requirement', 'Event', 'Quantity', 'Cost', 'DueDate', 'Status']
      }
    ]
  },
  {
    id: 'facilities',
    name: 'Facilities Services',
    icon: '🔧',
    description: 'Sites, assets, services, requirements, compliance, work orders.',
    tables: [
      {
        name: 'Sites',
        fields: ['SiteName', 'Address', 'SiteType', 'ServiceLevel', 'ContractReference', 'Status']
      },
      {
        name: 'Assets',
        fields: ['AssetName', 'AssetType', 'Site', 'SerialNumber', 'PurchaseCost', 'MaintenanceCost', 'InstallationDate', 'InspectionDate', 'MaintenanceDate', 'Status']
      },
      {
        name: 'Services',
        fields: ['ServiceName', 'ServiceType', 'Frequency', 'StandardPrice', 'StandardCost', 'SLA', 'Status']
      },
      {
        name: 'ServiceRequirements',
        fields: ['Site', 'Service', 'Frequency', 'RequiredStaff', 'EstimatedCost', 'Status']
      },
      {
        name: 'Compliance',
        fields: ['Site', 'Requirement', 'ResponsiblePerson', 'ReviewDate', 'ExpiryDate', 'Status', 'Documentation', 'Cost']
      },
      {
        name: 'WorkOrders',
        fields: ['Site', 'Asset', 'Service', 'Cost', 'Priority', 'DueDate', 'Status']
      }
    ]
  },
  {
    id: 'hospitality',
    name: 'Hospitality',
    icon: '🍽️',
    description: 'Locations, bookings, events, menu items, suppliers.',
    tables: [
      {
        name: 'Locations',
        fields: ['LocationName', 'Address', 'Capacity', 'OpeningHours', 'Manager', 'Status']
      },
      {
        name: 'Bookings',
        fields: ['Location', 'Date', 'Time', 'PartySize', 'BookingType', 'Value', 'Cost', 'Status', 'Notes']
      },
      {
        name: 'Events',
        fields: ['EventName', 'Location', 'Date', 'Capacity', 'Price', 'Cost', 'Revenue', 'Status']
      },
      {
        name: 'MenuItems',
        fields: ['ItemName', 'Category', 'Price', 'Cost', 'Margin', 'Availability', 'DietaryInformation', 'Status']
      },
      {
        name: 'Suppliers',
        fields: ['SupplierName', 'Category', 'ProductsServices', 'UnitCost', 'ContractCost', 'DeliverySchedule', 'Status']
      }
    ]
  },
  {
    id: 'restaurants',
    name: 'Restaurants',
    icon: '🍴',
    description: 'Locations, menu items, bookings, suppliers, promotions.',
    tables: [
      {
        name: 'Locations',
        fields: ['RestaurantName', 'Address', 'Capacity', 'OpeningHours', 'Manager', 'Status']
      },
      {
        name: 'MenuItems',
        fields: ['ItemName', 'Category', 'SellingPrice', 'IngredientCost', 'GrossMargin', 'DietaryInformation', 'Allergens', 'Availability', 'Status']
      },
      {
        name: 'Bookings',
        fields: ['Date', 'Time', 'PartySize', 'Table', 'BookingType', 'Value', 'Status', 'Notes']
      },
      {
        name: 'Suppliers',
        fields: ['SupplierName', 'Category', 'Product', 'UnitCost', 'DeliverySchedule', 'Status']
      },
      {
        name: 'Promotions',
        fields: ['PromotionName', 'MenuItem', 'StartDate', 'EndDate', 'Discount', 'RevenueImpact', 'Status']
      }
    ]
  },
  {
    id: 'marketing',
    name: 'Marketing & Advertising',
    icon: '📢',
    description: 'Campaigns, content, assets, ad variants, retainers.',
    tables: [
      {
        name: 'Campaigns',
        fields: ['CampaignName', 'Channel', 'Objective', 'Budget', 'PlannedRevenue', 'ActualRevenue', 'Spend', 'StartDate', 'EndDate', 'Status']
      },
      {
        name: 'Content',
        fields: ['ContentName', 'ContentType', 'Channel', 'Campaign', 'ProductionCost', 'Status', 'PublicationDate', 'Owner']
      },
      {
        name: 'Assets',
        fields: ['AssetName', 'AssetType', 'Campaign', 'Version', 'ProductionCost', 'ApprovalStatus', 'PublishedDate']
      },
      {
        name: 'AdVariants',
        fields: ['Campaign', 'Platform', 'Variant', 'Spend', 'Impressions', 'Clicks', 'Leads', 'Conversions', 'Revenue', 'Status']
      },
      {
        name: 'Retainers',
        fields: ['Client', 'MonthlyValue', 'MonthlyCost', 'StartDate', 'RenewalDate', 'Status']
      }
    ]
  },
  {
    id: 'retail',
    name: 'Retail',
    icon: '🛍️',
    description: 'Products, stock, promotions, suppliers, purchase orders.',
    tables: [
      {
        name: 'Products',
        fields: ['ProductName', 'SKU', 'Category', 'Supplier', 'SellingPrice', 'UnitCost', 'Margin', 'Status']
      },
      {
        name: 'StockLocations',
        fields: ['Location', 'Product', 'Quantity', 'ReorderLevel', 'UnitCost', 'StockValue', 'Status']
      },
      {
        name: 'Promotions',
        fields: ['PromotionName', 'Product', 'Discount', 'StartDate', 'EndDate', 'RevenueImpact', 'Status']
      },
      {
        name: 'Suppliers',
        fields: ['SupplierName', 'Product', 'UnitCost', 'LeadTime', 'MinimumOrder', 'Status']
      },
      {
        name: 'PurchaseOrders',
        fields: ['Supplier', 'Products', 'OrderValue', 'OrderDate', 'ExpectedDelivery', 'Status']
      }
    ]
  },
  {
    id: 'staffing',
    name: 'Staffing & Recruiting',
    icon: '👥',
    description: 'Candidates, vacancies, interviews, placements, qualifications.',
    tables: [
      {
        name: 'Candidates',
        fields: ['CandidateName', 'Skills', 'Qualifications', 'Location', 'Availability', 'ExpectedSalary', 'CandidateStatus', 'Source']
      },
      {
        name: 'Vacancies',
        fields: ['Role', 'RequiredSkills', 'Location', 'Salary', 'StartDate', 'ClosingDate', 'Status']
      },
      {
        name: 'Interviews',
        fields: ['Candidate', 'Vacancy', 'Date', 'Interviewer', 'Outcome', 'Feedback', 'Status']
      },
      {
        name: 'Placements',
        fields: ['Candidate', 'Vacancy', 'StartDate', 'EndDate', 'PlacementFee', 'ExpectedRevenue', 'Cost', 'Margin', 'Status']
      },
      {
        name: 'Qualifications',
        fields: ['Candidate', 'Qualification', 'IssuingBody', 'IssueDate', 'ExpiryDate', 'Status', 'Cost']
      }
    ]
  },
  {
    id: 'utilities',
    name: 'Utilities',
    icon: '💡',
    description: 'Sites, assets, service areas, inspections, service requests.',
    tables: [
      {
        name: 'Sites',
        fields: ['SiteName', 'Address', 'SiteType', 'Region', 'Status']
      },
      {
        name: 'Assets',
        fields: ['AssetName', 'Type', 'Site', 'SerialNumber', 'InstallationCost', 'MaintenanceCost', 'InspectionDate', 'Status']
      },
      {
        name: 'ServiceAreas',
        fields: ['AreaName', 'Region', 'ServiceType', 'Capacity', 'Status']
      },
      {
        name: 'Inspections',
        fields: ['Asset', 'Site', 'InspectionType', 'Date', 'Cost', 'Result', 'NextInspection', 'Status']
      },
      {
        name: 'ServiceRequests',
        fields: ['Site', 'RequestType', 'Priority', 'EstimatedCost', 'ActualCost', 'Status']
      }
    ]
  },
  {
    id: 'banking',
    name: 'Banking',
    icon: '🏦',
    description: 'Branches, products, cases, compliance, initiatives.',
    tables: [
      {
        name: 'Branches',
        fields: ['BranchName', 'Location', 'OpeningHours', 'Capacity', 'OperatingCost', 'Status']
      },
      {
        name: 'BankingProducts',
        fields: ['ProductName', 'ProductType', 'Rate', 'Eligibility', 'Price', 'Cost', 'Status']
      },
      {
        name: 'Cases',
        fields: ['CaseType', 'Priority', 'AssignedStaff', 'OpenDate', 'DueDate', 'Status', 'Cost']
      },
      {
        name: 'ComplianceReviews',
        fields: ['ReviewType', 'Date', 'Cost', 'DueDate', 'Status', 'Documentation']
      },
      {
        name: 'Initiatives',
        fields: ['Initiative', 'Department', 'Budget', 'ActualCost', 'StartDate', 'EndDate', 'Status']
      }
    ]
  },
  {
    id: 'education',
    name: 'Educational Management',
    icon: '📚',
    description: 'Courses, modules, classrooms, assessments, programmes.',
    tables: [
      {
        name: 'Courses',
        fields: ['CourseName', 'CourseCode', 'Department', 'Duration', 'Capacity', 'Price', 'DeliveryCost', 'Status']
      },
      {
        name: 'Modules',
        fields: ['ModuleName', 'Course', 'ModuleCode', 'Duration', 'Price', 'Cost', 'Status']
      },
      {
        name: 'Classrooms',
        fields: ['Room', 'Building', 'Capacity', 'Equipment', 'Cost', 'Availability', 'Status']
      },
      {
        name: 'Assessments',
        fields: ['Course', 'Module', 'AssessmentType', 'Date', 'Cost', 'Status']
      },
      {
        name: 'Programmes',
        fields: ['ProgrammeName', 'Type', 'Duration', 'Capacity', 'Price', 'Cost', 'Status']
      }
    ]
  },
  {
    id: 'executive-office',
    name: 'Executive Office',
    icon: '🏛️',
    description: 'Initiatives, decisions, risks, policies, strategic objectives.',
    tables: [
      {
        name: 'Initiatives',
        fields: ['Initiative', 'Objective', 'Owner', 'Budget', 'ActualCost', 'StartDate', 'TargetDate', 'Status']
      },
      {
        name: 'Decisions',
        fields: ['Decision', 'Date', 'DecisionMaker', 'RelatedInitiative', 'Outcome', 'Status', 'Cost']
      },
      {
        name: 'Risks',
        fields: ['Risk', 'Impact', 'Likelihood', 'PotentialCost', 'Owner', 'Mitigation', 'Status']
      },
      {
        name: 'Policies',
        fields: ['Policy', 'Version', 'Owner', 'ReviewDate', 'Status', 'Cost']
      },
      {
        name: 'StrategicObjectives',
        fields: ['Objective', 'KPI', 'Target', 'CurrentValue', 'Owner', 'Deadline', 'Status']
      }
    ]
  },
  {
    id: 'estate-agency',
    name: 'Estate Agency / Property',
    icon: '🏡',
    description: 'Properties, listings, viewings, offers, tenancies, maintenance.',
    tables: [
      {
        name: 'Properties',
        fields: ['PropertyAddress', 'PropertyType', 'Bedrooms', 'Bathrooms', 'AskingPrice', 'EstimatedValue', 'ListingDate', 'Status', 'Agent', 'Commission']
      },
      {
        name: 'Listings',
        fields: ['Property', 'Portal', 'ListingDate', 'ListingCost', 'Enquiries', 'Viewings', 'Offers', 'Status']
      },
      {
        name: 'Viewings',
        fields: ['Property', 'Date', 'Time', 'ViewingType', 'Status', 'Feedback', 'Cost']
      },
      {
        name: 'Offers',
        fields: ['Property', 'OfferValue', 'OfferDate', 'Status', 'Buyer', 'Notes', 'Commission']
      },
      {
        name: 'Tenancies',
        fields: ['Property', 'Rent', 'Deposit', 'StartDate', 'EndDate', 'RenewalDate', 'ManagementFee', 'Status']
      },
      {
        name: 'Maintenance',
        fields: ['Property', 'Issue', 'Priority', 'EstimatedCost', 'ActualCost', 'Status', 'CompletionDate']
      }
    ]
  },
  {
    id: 'professional-services',
    name: 'Professional Services',
    icon: '💼',
    description: 'Engagements, matters, deliverables, documents, compliance.',
    tables: [
      {
        name: 'Engagements',
        fields: ['EngagementName', 'Type', 'StartDate', 'TargetDate', 'Budget', 'ExpectedRevenue', 'ActualCost', 'Margin', 'Status']
      },
      {
        name: 'Matters',
        fields: ['MatterName', 'Type', 'Priority', 'ResponsibleStaff', 'Deadline', 'Status', 'Fee', 'Cost']
      },
      {
        name: 'Deliverables',
        fields: ['Engagement', 'Deliverable', 'DueDate', 'Fee', 'Cost', 'Status', 'ApprovalStatus']
      },
      {
        name: 'Documents',
        fields: ['Document', 'Type', 'RelatedEngagement', 'Version', 'ReviewDate', 'Status', 'Cost']
      },
      {
        name: 'Compliance',
        fields: ['Requirement', 'RelatedEngagement', 'Deadline', 'Cost', 'Status']
      }
    ]
  },
  {
    id: 'accounting',
    name: 'Accounting',
    icon: '📊',
    description: 'Accounts, tax returns, payroll runs, compliance, documents.',
    tables: [
      {
        name: 'Accounts',
        fields: ['AccountingPeriod', 'YearEnd', 'VATScheme', 'VATPeriod', 'Deadline', 'Fee', 'Cost', 'Status']
      },
      {
        name: 'TaxReturns',
        fields: ['TaxType', 'Period', 'FilingDeadline', 'PreparationStatus', 'SubmissionDate', 'Fee', 'Cost', 'Status']
      },
      {
        name: 'PayrollRuns',
        fields: ['PayPeriod', 'PayDate', 'ProcessingStatus', 'ApprovalStatus', 'Cost', 'Status']
      },
      {
        name: 'ComplianceReviews',
        fields: ['ReviewType', 'ReviewDate', 'Deadline', 'Fee', 'Cost', 'Status']
      },
      {
        name: 'Documents',
        fields: ['DocumentType', 'Period', 'ReceivedDate', 'ReviewDate', 'Status', 'Cost']
      }
    ]
  },
  {
    id: 'collections',
    name: 'Collections / Credit Control',
    icon: '💳',
    description: 'Cases, communications, payment plans, documents.',
    tables: [
      {
        name: 'Cases',
        fields: ['CaseReference', 'CaseType', 'Priority', 'OpenDate', 'DueDate', 'AmountDue', 'AmountRecovered', 'Status']
      },
      {
        name: 'Communications',
        fields: ['Case', 'CommunicationType', 'Date', 'Outcome', 'FollowUpDate', 'Status', 'Cost']
      },
      {
        name: 'PaymentPlans',
        fields: ['Case', 'AgreedAmount', 'Frequency', 'StartDate', 'EndDate', 'Status']
      },
      {
        name: 'Documents',
        fields: ['Case', 'DocumentType', 'Date', 'Status', 'Cost']
      }
    ]
  },
  {
    id: 'software-technology',
    name: 'Software & Technology',
    icon: '💻',
    description: 'Products, subscriptions, projects, releases, licences, assets, support contracts.',
    tables: [
      {
        name: 'Products',
        fields: ['ProductName', 'ProductType', 'Description', 'Version', 'Price', 'UnitCost', 'Margin', 'Status']
      },
      {
        name: 'Subscriptions',
        fields: ['Product', 'Plan', 'Customer', 'BillingFrequency', 'Price', 'StartDate', 'RenewalDate', 'CancellationDate', 'Status']
      },
      {
        name: 'SoftwareProjects',
        fields: ['ProjectName', 'Client', 'Technology', 'StartDate', 'TargetDate', 'QuotedPrice', 'EstimatedCost', 'ActualCost', 'ExpectedMargin', 'Status']
      },
      {
        name: 'DevelopmentReleases',
        fields: ['Product', 'Version', 'ReleaseDate', 'ReleaseType', 'DevelopmentCost', 'Status']
      },
      {
        name: 'SoftwareLicences',
        fields: ['SoftwareName', 'Provider', 'LicenceType', 'NumberOfLicences', 'CostPerLicence', 'TotalCost', 'RenewalDate', 'Department', 'Status']
      },
      {
        name: 'ITAssets',
        fields: ['AssetName', 'AssetType', 'SerialNumber', 'AssignedTo', 'Location', 'PurchaseCost', 'WarrantyExpiry', 'MaintenanceCost', 'ReplacementDate', 'Status']
      },
      {
        name: 'SupportContracts',
        fields: ['ContractName', 'Customer', 'ContractType', 'StartDate', 'EndDate', 'RenewalDate', 'ContractValue', 'SupportCost', 'SLA', 'Status']
      }
    ]
  }
];

const getTemplateForIndustry = (industry) => {
  const normalizedIndustry = toSchemaKey(industry);
  return TEMPLATES.find((template) => {
    const templateName = toSchemaKey(template.name);
    return normalizedIndustry && (templateName.includes(normalizedIndustry) || normalizedIndustry.includes(templateName) || templateName.split('_').some((word) => word.length > 3 && normalizedIndustry.includes(word)));
  }) || null;
};

// ------------------------------------------------------------
// SCHEMA EDITOR – redesigned to match admin dark/glass style
// ------------------------------------------------------------
const TABLE_COLORS = [
  { bg: 'bg-blue-950/20', border: 'border-blue-800/40', header: 'bg-blue-900/40 text-blue-200' },
  { bg: 'bg-emerald-950/20', border: 'border-emerald-800/40', header: 'bg-emerald-900/40 text-emerald-200' },
  { bg: 'bg-purple-950/20', border: 'border-purple-800/40', header: 'bg-purple-900/40 text-purple-200' },
  { bg: 'bg-amber-950/20', border: 'border-amber-800/40', header: 'bg-amber-900/40 text-amber-200' },
  { bg: 'bg-rose-950/20', border: 'border-rose-800/40', header: 'bg-rose-900/40 text-rose-200' },
  { bg: 'bg-cyan-950/20', border: 'border-cyan-800/40', header: 'bg-cyan-900/40 text-cyan-200' },
  { bg: 'bg-indigo-950/20', border: 'border-indigo-800/40', header: 'bg-indigo-900/40 text-indigo-200' },
  { bg: 'bg-teal-950/20', border: 'border-teal-800/40', header: 'bg-teal-900/40 text-teal-200' },
];

function SchemaEditor({ initialSchema, onSave, onBack, companyName, companyIndustry, isTemplate, selectedTables = [] }) {
  const [rows, setRows] = useState(() => {
    return initialSchema.map((field) => ({
      id: Date.now().toString() + Math.random().toString(36).substr(2, 6),
      name: field.name || '',
      type: field.type || 'TEXT',
      sample: field.sample || '',
      confirmed: false,
      table: field.table || null,
      financial: field.financial || false,
      financialDirection: inferFinancialDirection(field),
      dashboard: field.dashboard !== false,
      referenceTableKey: field.referenceTableKey || null,
    }));
  });

  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  const tableList = isTemplate ? selectedTables : [];

  const addRow = () => {
    const defaultTable = tableList.length > 0 ? tableList[0] : null;
    setRows([
      ...rows,
      {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 6),
        name: '',
        type: 'TEXT',
        sample: '',
        confirmed: false,
        table: defaultTable,
        financial: false,
        financialDirection: 'neutral',
        dashboard: true,
        referenceTableKey: null,
      },
    ]);
  };

  const deleteRow = (id) => {
    setRows(rows.filter((row) => row.id !== id));
    setShowDeleteConfirm(null);
  };

  const updateRow = (id, field, value) => {
    setRows(rows.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
  };

  const toggleConfirm = (id) => {
    setRows(rows.map((row) => (row.id === id ? { ...row, confirmed: !row.confirmed } : row)));
  };

  const confirmTableRows = (tableName) => {
    setRows(rows.map((row) => (
      (row.table || 'General') === tableName ? { ...row, confirmed: true } : row
    )));
  };

  const allConfirmed = rows.length > 0 && rows.every((row) => row.confirmed);

  const groupedRows = rows.reduce((acc, row) => {
    const table = row.table || 'General';
    if (!acc[table]) acc[table] = [];
    acc[table].push(row);
    return acc;
  }, {});

  const sortedTableNames = isTemplate
    ? tableList.filter((t) => groupedRows[t])
    : Object.keys(groupedRows);

  const handleSave = async () => {
    if (!allConfirmed) return;
    setIsSaving(true);
    try {
      const tableMap = rows.reduce((tables, row) => {
        const tableName = row.table || 'Imported Data';
        tables[tableName] ??= { name: tableName, fields: [] };
        tables[tableName].fields.push({
          name: row.name,
          type: row.type,
          sample: row.sample,
          financial: row.financial,
          financialDirection: row.financialDirection,
          dashboard: row.dashboard,
          referenceTableKey: row.referenceTableKey,
        });
        return tables;
      }, {});
      const payload = {
        companyName,
        source: isTemplate ? 'template' : 'upload',
        tables: Object.values(tableMap),
      };
      const res = await fetch(`${MONGO_API_BASE}/api/data-layer/schema`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('accessToken')
            ? { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
            : {}),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message || 'Failed to save schema');
      }
      const data = await res.json();
      const savedSchema = data?.data?.active;
      const planRes = await fetch(`${MONGO_API_BASE}/api/data-layer/schema/plan`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('accessToken')
            ? { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
            : {}),
        },
        body: JSON.stringify({
          industry: companyIndustry || '',
          draft: {
            companyName,
            source: isTemplate ? 'template' : 'upload',
            tables: savedSchema?.tables || payload.tables,
          },
        }),
      });
      const planData = await planRes.json();
      if (!planRes.ok) throw new Error(planData?.message || 'Failed to plan dashboard schema');

      const plannedRes = await fetch(`${MONGO_API_BASE}/api/data-layer/schema`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('accessToken')
            ? { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
            : {}),
        },
        body: JSON.stringify(planData.data),
      });
      const plannedData = await plannedRes.json();
      if (!plannedRes.ok) throw new Error(plannedData?.message || 'Failed to save dashboard schema');
      onSave(plannedData?.data?.active || savedSchema || null);
    } catch (error) {
      console.error(error);
      alert('Failed to save schema. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const renderTableGroup = (tableName, tableRows, colorIndex) => {
    const color = TABLE_COLORS[colorIndex % TABLE_COLORS.length];
    return (
      <div key={tableName} className={`rounded-xl border ${color.border} ${color.bg} shadow-sm overflow-hidden`}>
        <div className={`px-4 py-3 ${color.header} border-b ${color.border} flex items-center justify-between`}>
          <h3 className="text-sm font-semibold">{tableName}</h3>
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium">
              {tableRows.filter((r) => r.confirmed).length} / {tableRows.length} confirmed
            </span>
            <button
              type="button"
              onClick={() => confirmTableRows(tableName)}
              disabled={tableRows.every((row) => row.confirmed)}
              className="rounded-md border border-current/30 px-2 py-1 text-xs font-semibold hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Confirm all
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700/40">
            <thead className="bg-gray-800/40">
              <tr>
                <th className="w-12 px-4 py-2 text-left text-xs font-medium text-gray-400">#</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400">Field Name</th>
                <th className="w-36 px-4 py-2 text-left text-xs font-medium text-gray-400">Data Type</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400">Sample Value</th>
                {isTemplate && (
                  <th className="w-24 px-4 py-2 text-center text-xs font-medium text-gray-400">Financial</th>
                )}
                <th className="w-32 px-4 py-2 text-left text-xs font-medium text-gray-400">Financial type</th>
                <th className="w-36 px-4 py-2 text-left text-xs font-medium text-gray-400">Links to</th>
                <th className="w-24 px-4 py-2 text-center text-xs font-medium text-gray-400">Dashboard</th>
                <th className="w-20 px-4 py-2 text-center text-xs font-medium text-gray-400">Confirm</th>
                <th className="w-12 px-4 py-2 text-center text-xs font-medium text-gray-400">Delete</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/30">
              {tableRows.map((row, index) => {
                const isConfirmed = row.confirmed;
                return (
                  <tr key={row.id} className={`transition ${isConfirmed ? 'bg-green-950/20' : 'hover:bg-gray-800/30'}`}>
                    <td className="px-4 py-2 text-sm text-gray-500">{index + 1}</td>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={row.name}
                        onChange={(e) => updateRow(row.id, 'name', e.target.value)}
                        className="w-full border-b border-transparent bg-transparent text-sm font-medium text-white focus:border-indigo-400 focus:outline-none"
                        placeholder="Enter field name"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <select
                        value={row.type}
                        onChange={(e) => updateRow(row.id, 'type', e.target.value)}
                        className="w-full rounded-md border-gray-700 bg-gray-800 text-sm text-white focus:border-indigo-500 focus:ring-indigo-500"
                      >
                        {DATA_TYPES.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={row.sample}
                        onChange={(e) => updateRow(row.id, 'sample', e.target.value)}
                        className="w-full border-b border-transparent bg-transparent text-sm text-gray-300 focus:border-indigo-400 focus:outline-none"
                        placeholder="Sample value"
                      />
                    </td>
                    {isTemplate && (
                      <td className="px-4 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={row.financial}
                          onChange={(e) => updateRow(row.id, 'financial', e.target.checked)}
                          className="rounded border-gray-600 bg-gray-800 text-indigo-500 focus:ring-indigo-500"
                        />
                      </td>
                    )}
                    <td className="px-4 py-2">
                      <select value={row.financialDirection} onChange={(e) => updateRow(row.id, 'financialDirection', e.target.value)} className="w-full rounded-md border-gray-700 bg-gray-800 text-sm text-white focus:border-indigo-500 focus:ring-indigo-500">
                        {FINANCIAL_DIRECTIONS.map((direction) => <option key={direction} value={direction}>{direction}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      <select value={row.referenceTableKey || ''} onChange={(e) => updateRow(row.id, 'referenceTableKey', e.target.value || null)} className="w-full rounded-md border-gray-700 bg-gray-800 text-sm text-white focus:border-indigo-500 focus:ring-indigo-500">
                        <option value="">No link</option>
                        {tableList.filter((name) => name !== tableName).map((name) => <option key={name} value={toSchemaKey(name)}>{name}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <input type="checkbox" checked={row.dashboard} onChange={(e) => updateRow(row.id, 'dashboard', e.target.checked)} className="rounded border-gray-600 bg-gray-800 text-indigo-500 focus:ring-indigo-500" />
                    </td>
                    <td className="px-4 py-2 text-center">
                      <button onClick={() => toggleConfirm(row.id)} className="focus:outline-none">
                        {isConfirmed ? (
                          <CheckCircleSolid className="mx-auto size-5 text-green-400" />
                        ) : (
                          <CheckCircleIcon className="mx-auto size-5 text-gray-500 hover:text-gray-300" />
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <button
                        onClick={() => setShowDeleteConfirm(row.id)}
                        className="rounded p-1 text-gray-500 hover:bg-red-950/30 hover:text-red-400"
                      >
                        <TrashIcon className="size-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Non‑template flat table
  const renderFlatTable = () => (
    <div className="overflow-hidden rounded-xl bg-gray-900/80 backdrop-blur-xl border border-white/10 shadow-xl">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-700/40">
          <thead className="bg-gray-800/50">
            <tr>
              <th className="w-12 px-4 py-3 text-left text-xs font-medium text-gray-400">#</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Field Name</th>
              <th className="w-40 px-4 py-3 text-left text-xs font-medium text-gray-400">Data Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Sample Value</th>
              <th className="w-24 px-4 py-3 text-center text-xs font-medium text-gray-400">Financial</th>
              <th className="w-32 px-4 py-3 text-left text-xs font-medium text-gray-400">Financial type</th>
              <th className="w-24 px-4 py-3 text-center text-xs font-medium text-gray-400">Dashboard</th>
              <th className="w-24 px-4 py-3 text-center text-xs font-medium text-gray-400">Confirm</th>
              <th className="w-12 px-4 py-3 text-center text-xs font-medium text-gray-400">Delete</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/30">
            {rows.map((row, index) => {
              const isConfirmed = row.confirmed;
              return (
                <tr key={row.id} className={`transition ${isConfirmed ? 'bg-green-950/20' : 'hover:bg-gray-800/30'}`}>
                  <td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
                  <td className="px-4 py-3">
                    <input
                      type="text"
                      value={row.name}
                      onChange={(e) => updateRow(row.id, 'name', e.target.value)}
                      className="w-full border-b border-transparent bg-transparent text-sm font-medium text-white focus:border-indigo-400 focus:outline-none"
                      placeholder="Enter field name"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={row.type}
                      onChange={(e) => updateRow(row.id, 'type', e.target.value)}
                      className="w-full rounded-md border-gray-700 bg-gray-800 text-sm text-white focus:border-indigo-500 focus:ring-indigo-500"
                    >
                      {DATA_TYPES.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="text"
                      value={row.sample}
                      onChange={(e) => updateRow(row.id, 'sample', e.target.value)}
                      className="w-full border-b border-transparent bg-transparent text-sm text-gray-300 focus:border-indigo-400 focus:outline-none"
                      placeholder="Sample value"
                    />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={row.financial}
                      onChange={(e) => updateRow(row.id, 'financial', e.target.checked)}
                      className="rounded border-gray-600 bg-gray-800 text-indigo-500 focus:ring-indigo-500"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <select value={row.financialDirection} onChange={(e) => updateRow(row.id, 'financialDirection', e.target.value)} className="w-full rounded-md border-gray-700 bg-gray-800 text-sm text-white focus:border-indigo-500 focus:ring-indigo-500">
                      {FINANCIAL_DIRECTIONS.map((direction) => <option key={direction} value={direction}>{direction}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={row.dashboard}
                      onChange={(e) => updateRow(row.id, 'dashboard', e.target.checked)}
                      className="rounded border-gray-600 bg-gray-800 text-indigo-500 focus:ring-indigo-500"
                    />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => toggleConfirm(row.id)} className="focus:outline-none">
                      {isConfirmed ? (
                        <CheckCircleSolid className="mx-auto size-6 text-green-400" />
                      ) : (
                        <CheckCircleIcon className="mx-auto size-6 text-gray-500 hover:text-gray-300" />
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => setShowDeleteConfirm(row.id)}
                      className="rounded p-1 text-gray-500 hover:bg-red-950/30 hover:text-red-400"
                    >
                      <TrashIcon className="size-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ------------------------------------------------------------
  // MAIN RENDER
  // ------------------------------------------------------------
  return (
    <div className="mt-8">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-white">
            {isTemplate ? 'Template Schema' : 'AI Detected Schema'}
          </h2>
          <p className="text-sm text-gray-400">
            {rows.length} field{rows.length > 1 ? 's' : ''} · {rows.filter((r) => r.confirmed).length} confirmed
          </p>
        </div>
        <button
          onClick={addRow}
          className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-r from-indigo-600 to-indigo-700 px-3 py-1.5 text-sm font-semibold text-white shadow-lg shadow-indigo-950/40 hover:from-indigo-500 hover:to-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <PlusIcon className="size-4" />
          Add Field
        </button>
      </div>

      {isTemplate ? (
        <div className="space-y-6">
          {sortedTableNames.map((tableName, idx) => renderTableGroup(tableName, groupedRows[tableName], idx))}
        </div>
      ) : (
        renderFlatTable()
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="rounded-xl bg-gray-900 border border-white/10 p-6 shadow-2xl max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-white">Delete Field?</h3>
            <p className="mt-2 text-sm text-gray-300">
              This action cannot be undone. Are you sure you want to delete this field?
            </p>
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="rounded-md px-4 py-2 text-sm font-medium text-gray-300 hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteRow(showDeleteConfirm)}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-500"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-6">
        <button
          onClick={onBack}
          className="text-sm text-gray-400 hover:text-gray-300"
        >
          ← Back
        </button>
        <div className="flex items-center gap-4">
          {!allConfirmed && rows.length > 0 && (
            <p className="text-sm text-yellow-400">
              Please confirm all fields before saving.
            </p>
          )}
          <button
            onClick={handleSave}
            disabled={!allConfirmed || isSaving}
            className={`inline-flex items-center rounded-md px-6 py-2.5 text-sm font-semibold shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
              allConfirmed && !isSaving
                ? 'bg-gradient-to-r from-emerald-600 to-indigo-700 text-white shadow-indigo-950/40 hover:from-indigo-500 hover:to-indigo-600 focus-visible:outline-indigo-500'
                : 'cursor-not-allowed bg-gray-700 text-gray-400'
            }`}
          >
            {isSaving ? (
              <>
                <span className="mr-2 inline-block size-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Saving...
              </>
            ) : (
              'Save Schema →'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// DATA TABLE WORKSPACE – also redesigned
// ------------------------------------------------------------
function DataTableWorkspace({ table, getAuthHeaders, onTableDeleted, onBack, onToggleSidebar, sidebarCollapsed }) {
  const [records, setRecords] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [editingRecord, setEditingRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [financialTotals, setFinancialTotals] = useState([]);
  const [recordSearch, setRecordSearch] = useState('');
  const [showRecordForm, setShowRecordForm] = useState(false);
  const [linkedOptions, setLinkedOptions] = useState({});
  const [staffOptions, setStaffOptions] = useState([]);
  const uploadRef = useRef(null);

  const loadRecords = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${MONGO_API_BASE}/api/data-layer/tables/${table.tableKey}/records`, {
        credentials: 'include', headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Failed to load records');
      setRecords(data.data || []);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const loadFinancialTotals = async () => {
    try {
      const res = await fetch(`${MONGO_API_BASE}/api/data-layer/dashboard`, {
        credentials: 'include', headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (res.ok) setFinancialTotals((data.data || []).filter((item) => item.tableKey === table.tableKey));
    } catch (error) {
      console.error('Failed to load financial totals:', error);
    }
  };

  useEffect(() => {
    setRecords([]);
    setFormValues({});
    setEditingRecord(null);
    setMessage('');
    loadRecords();
    loadFinancialTotals();
  }, [table.tableKey]);

  useEffect(() => {
    const linkedFields = table.fields.filter((field) => field.reference?.source === 'data-layer' || field.referenceTableKey);
    Promise.all(linkedFields.map(async (field) => {
      const tableKey = field.reference?.tableKey || field.referenceTableKey;
      const res = await fetch(`${MONGO_API_BASE}/api/data-layer/tables/${tableKey}/options`, { credentials: 'include', headers: getAuthHeaders() });
      const data = await res.json();
      return [field.fieldKey, res.ok ? data.data || [] : []];
    })).then((entries) => setLinkedOptions(Object.fromEntries(entries))).catch(() => setLinkedOptions({}));
  }, [table.tableKey]);

  useEffect(() => {
    if (!table.fields.some((field) => field.reference?.source === 'staff')) return;
    fetch('https://api.insightos.co.uk/api/staff', { credentials: 'include' })
      .then((res) => res.json())
      .then((payload) => setStaffOptions((Array.isArray(payload?.data) ? payload.data : []).map((entry) => ({ value: String(entry?.id ?? ''), label: String(entry?.Name || entry?.name || '').trim() })).filter((entry) => entry.value && entry.label)))
      .catch(() => setStaffOptions([]));
  }, [table.tableKey]);

  const saveRecord = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setMessage('');
    try {
      const endpoint = editingRecord
        ? `${MONGO_API_BASE}/api/data-layer/tables/${table.tableKey}/records/${editingRecord.recordId}`
        : `${MONGO_API_BASE}/api/data-layer/tables/${table.tableKey}/records`;
      const res = await fetch(endpoint, {
        method: editingRecord ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ values: formValues }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Failed to save record');
      setFormValues({});
      setEditingRecord(null);
      setShowRecordForm(false);
      setMessage('Record saved.');
      await Promise.all([loadRecords(), loadFinancialTotals()]);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const deleteRecord = async (recordId) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      const res = await fetch(`${MONGO_API_BASE}/api/data-layer/tables/${table.tableKey}/records/${recordId}`, {
        method: 'DELETE', credentials: 'include', headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to delete record');
      await Promise.all([loadRecords(), loadFinancialTotals()]);
    } catch (error) {
      setMessage(error.message);
    }
  };

  const deleteTable = async () => {
    if (!window.confirm(`Delete ${table.name} and every record in it? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${MONGO_API_BASE}/api/data-layer/tables/${table.tableKey}`, { method: 'DELETE', credentials: 'include', headers: getAuthHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Failed to delete table');
      onTableDeleted(data.data.active);
    } catch (error) {
      setMessage(error.message);
    }
  };

  const importFile = async (file) => {
    if (!file) return;
    setMessage('Importing data...');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${MONGO_API_BASE}/api/data-layer/tables/${table.tableKey}/import`, {
        method: 'POST', credentials: 'include', headers: getAuthHeaders(), body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Import failed');
      setMessage(`${data.insertedCount} records imported.`);
      await Promise.all([loadRecords(), loadFinancialTotals()]);
    } catch (error) {
      setMessage(error.message);
    } finally {
      if (uploadRef.current) uploadRef.current.value = '';
    }
  };

  const inputType = (field) => {
    if (field.type === 'DATE') return 'date';
    if (field.type === 'TIME') return 'time';
    if (field.type === 'DATETIME') return 'datetime-local';
    if (['INT', 'DECIMAL', 'CURRENCY'].includes(field.type)) return 'number';
    if (field.type === 'EMAIL') return 'email';
    if (field.type === 'URL') return 'url';
    return 'text';
  };

  const revenueTotal = financialTotals.filter((item) => item.financialDirection === 'revenue').reduce((sum, item) => sum + Number(item.total || 0), 0);
  const expenseTotal = financialTotals.filter((item) => item.financialDirection === 'expense').reduce((sum, item) => sum + Number(item.total || 0), 0);
  const financialTotal = revenueTotal - expenseTotal;
  const stats = [
    { name: 'Total records', stat: records.length.toLocaleString() },
    { name: 'Financial summaries', stat: financialTotals.length.toString() },
    { name: financialTotals.some((item) => item.aggregation === 'monthly_recurring_revenue') ? 'Active monthly revenue' : 'Net revenue', stat: new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(financialTotal) },
  ];

  const activityIcon = (record) => {
    const financialRecord = table.fields.some((field) => field.financial && Number(record.values?.[field.fieldKey] || 0) > 0);
    return financialRecord ? ArrowUpCircleIcon : ArrowPathIcon;
  };

  const filteredRecords = records.filter((record) => {
    const query = recordSearch.trim().toLowerCase();
    if (!query) return true;
    return table.fields.some((field) => String(record.values?.[field.fieldKey] ?? '').toLowerCase().includes(query));
  });
  return (
    <section className="space-y-6 rounded-2xl border border-white/10 bg-gray-900/80 p-4 shadow-xl shadow-black/20 backdrop-blur-xl sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-white/10 pb-5">
        <div>
          <div className="mb-3 flex items-center gap-2">
            <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-400 hover:text-indigo-300">
              <ChevronLeftIcon className="size-4" /> All tables
            </button>
            <button type="button" onClick={onToggleSidebar} className="rounded-md border border-indigo-800 bg-gray-800/60 px-2 py-1 text-xs font-semibold text-indigo-300 hover:bg-gray-700">
              {sidebarCollapsed ? 'Show tables' : 'Full screen'}
            </button>
          </div>
          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-400">Data Layer</p>
          <h2 className="mt-1 text-2xl font-semibold text-white">{table.name}</h2>
          <p className="mt-1 text-sm text-gray-400">{table.fields.length} fields · {records.length} records</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => { setEditingRecord(null); setFormValues({}); setShowRecordForm(true); }} className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-md shadow-md shadow-indigo-950/40 px-4 py-2 text-sm font-semibold text-white shadow-lg hover:from-emerald-500 hover:to-emerald-600">
            <PlusIcon className="size-4" /> New record
          </button>
          <label className="inline-flex cursor-pointer items-center gap-2 border border-indigo-800 bg-indigo-700 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-700 rounded-md shadow-md">
            <DocumentArrowUpIcon className="size-4" /> Import
            <input ref={uploadRef} type="file" accept=".csv,.xls,.xlsx" className="sr-only" onChange={(event) => importFile(event.target.files?.[0])} />
          </label>
          <button type="button" onClick={deleteTable} className="inline-flex items-center gap-2 border border-red-800 bg-gray-800/60 px-3 py-2 text-sm font-semibold text-red-400 hover:bg-gray-700">
            <TrashIcon className="size-4" /> Delete table
          </button>
        </div>
      </div>

      <div>
        <h3 className="text-base font-semibold text-white">Overview</h3>
        <dl className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
          {stats.map((item) => (
            <div key={item.name} className="overflow-hidden rounded-lg bg-gray-800/60 px-4 py-5 shadow-sm ring-1 ring-white/10 sm:p-6">
              <dt className="truncate text-sm font-medium text-gray-400">{item.name}</dt>
              <dd className="mt-1 text-3xl font-semibold tracking-tight text-white">{item.stat}</dd>
            </div>
          ))}
        </dl>
      </div>

      {financialTotals.length > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {financialTotals.map((item) => (
            <div key={item.fieldKey} className={`rounded-lg px-4 py-5 shadow-sm ring-1 sm:p-6 ${item.financialDirection === 'expense' ? 'bg-red-950/30 ring-red-800/60' : 'bg-emerald-950/30 ring-emerald-800/60'}`}>
              <p className={`truncate text-sm font-medium ${item.financialDirection === 'expense' ? 'text-red-300' : 'text-emerald-300'}`}>{formatFieldLabel(item.fieldName)}</p>
              <p className={`mt-1 text-2xl font-semibold tracking-tight ${item.financialDirection === 'expense' ? 'text-red-100' : 'text-emerald-100'}`}>{new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(item.total)}</p>
              {item.aggregation === 'monthly_recurring_revenue' && <p className="mt-1 text-xs text-emerald-300">Scheduled monthly revenue: {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(item.scheduledTotal || 0)}</p>}
            </div>
          ))}
        </div>
      )}

      {showRecordForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="record-form-title">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-6">
            <button type="button" className="fixed inset-0 bg-gray-900/75" aria-label="Close record form" onClick={() => { setEditingRecord(null); setFormValues({}); setShowRecordForm(false); }} />
            <form onSubmit={saveRecord} className="relative w-full max-w-4xl overflow-hidden rounded-lg bg-white text-left shadow-2xl">
              <div className="border-b border-gray-200 px-6 py-5">
                <h3 id="record-form-title" className="text-lg font-semibold text-gray-900">{editingRecord ? 'Edit record' : `Add ${table.name} record`}</h3>
                <p className="mt-1 text-sm text-gray-500">Enter the details for this record.</p>
              </div>
              <div className="max-h-[60vh] overflow-y-auto px-6 py-5">
                <div className="grid gap-x-6 gap-y-5 sm:grid-cols-2">
                  {table.fields.map((field) => (
                    <label key={field.fieldKey} className="block text-sm font-medium text-gray-700">
                      {formatFieldLabel(field.name)}
                      {field.reference?.source === 'staff' ? (
                        <select value={formValues[field.fieldKey] ?? ''} onChange={(event) => setFormValues({ ...formValues, [field.fieldKey]: event.target.value })} className="mt-2 block w-full rounded-md border-gray-300 bg-white text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"><option value="">Select staff member</option>{staffOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select>
                      ) : (field.reference?.source === 'data-layer' || field.referenceTableKey) ? (
                        <select value={formValues[field.fieldKey] ?? ''} onChange={(event) => setFormValues({ ...formValues, [field.fieldKey]: event.target.value })} className="mt-2 block w-full rounded-md border-gray-300 bg-white text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"><option value="">Select {field.name}</option>{(linkedOptions[field.fieldKey] || []).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select>
                      ) : field.type === 'SELECT' ? (
                        <select value={formValues[field.fieldKey] ?? ''} onChange={(event) => setFormValues({ ...formValues, [field.fieldKey]: event.target.value })} className="mt-2 block w-full rounded-md border-gray-300 bg-white text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"><option value="">Select {field.name}</option>{(field.options || []).map((option) => <option key={option} value={option}>{option}</option>)}</select>
                      ) : field.type === 'BOOLEAN' ? (
                        <select value={formValues[field.fieldKey] ?? ''} onChange={(event) => setFormValues({ ...formValues, [field.fieldKey]: event.target.value })} className="mt-2 block w-full rounded-md border-gray-300 bg-white text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"><option value="">Not set</option><option value="true">Yes</option><option value="false">No</option></select>
                      ) : field.type === 'TEXTAREA' ? (
                        <textarea rows={4} value={formValues[field.fieldKey] ?? ''} onChange={(event) => setFormValues({ ...formValues, [field.fieldKey]: event.target.value })} className="mt-2 block w-full rounded-md border-gray-300 bg-white text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
                      ) : field.type === 'FORMULA' ? (
                        <input value="Calculated when the record is saved" readOnly className="mt-2 block w-full rounded-md border-gray-300 bg-gray-50 text-sm text-gray-500 shadow-sm" />
                      ) : (
                        <input type={inputType(field)} step={['INT', 'DECIMAL', 'CURRENCY'].includes(field.type) ? 'any' : undefined} value={formValues[field.fieldKey] ?? ''} onChange={(event) => setFormValues({ ...formValues, [field.fieldKey]: event.target.value })} className="mt-2 block w-full rounded-md border-gray-300 bg-white text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
                      )}
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex flex-row-reverse gap-3 bg-gray-50 px-6 py-4">
                <button disabled={isSaving} className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"><PlusIcon className="size-4" /> {isSaving ? 'Saving...' : editingRecord ? 'Save changes' : 'Add record'}</button>
                <button type="button" onClick={() => { setEditingRecord(null); setFormValues({}); setShowRecordForm(false); }} className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {message && <p className="text-sm font-medium text-indigo-300">{message}</p>}

      <section className="overflow-hidden rounded-lg bg-gray-900/80 backdrop-blur-xl border border-white/10 shadow-xl">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-4 sm:px-6">
          <div>
            <h3 className="text-base font-semibold text-white">Records</h3>
            <p className="mt-1 text-xs text-gray-400">Showing {filteredRecords.length} of {records.length} records</p>
          </div>
          <label className="relative block w-full sm:w-72">
            <span className="sr-only">Search records</span>
            <input value={recordSearch} onChange={(event) => setRecordSearch(event.target.value)} placeholder="Search records..." className="block w-full rounded-md border-gray-700 bg-gray-800 py-2 pl-3 pr-3 text-sm text-white shadow-sm placeholder:text-gray-400 focus:border-indigo-500 focus:ring-indigo-500" />
          </label>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto text-left" style={{ minWidth: `${Math.max(900, table.fields.length * 168 + 150)}px` }}>
            <thead className="bg-gray-800/50">
              <tr>
                {table.fields.map((field) => (
                  <th key={field.fieldKey} scope="col" className="max-w-[210px] whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-400 sm:px-6">{formatFieldLabel(field.name)}</th>
                ))}
                <th scope="col" className="sticky right-0 z-10 w-56 whitespace-nowrap bg-gray-800/50 px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {isLoading ? (
                <tr><td colSpan={table.fields.length + 1} className="px-6 py-12 text-center text-sm text-gray-400">Loading records...</td></tr>
              ) : filteredRecords.length === 0 ? (
                <tr><td colSpan={table.fields.length + 1} className="px-6 py-12 text-center text-sm text-gray-400">{records.length ? 'No records match your search.' : 'No records yet.'}</td></tr>
              ) : filteredRecords.map((record) => (
                <tr key={record.recordId} className="group hover:bg-indigo-950/20">
                  {table.fields.map((field) => (
                    <td key={field.fieldKey} title={String(record.values?.[field.fieldKey] ?? '')} className="max-w-[210px] truncate whitespace-nowrap px-4 py-4 text-sm text-gray-200 sm:px-6">{String(record.values?.[field.fieldKey] ?? '-')}</td>
                  ))}
                  <td className="sticky right-0 whitespace-nowrap bg-gray-900/80 px-4 py-4 text-right text-sm group-hover:bg-indigo-950/20">
                    <button type="button" className="font-medium text-indigo-400 hover:text-indigo-300" onClick={() => { setEditingRecord(record); setFormValues(record.values || {}); setShowRecordForm(true); }}>Edit</button>
                    <button type="button" className="ml-3 font-medium text-red-400 hover:text-red-300" onClick={() => deleteRecord(record.recordId)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </section>
  );
}

function DataLayerDashboard({ activeSchema, companyIndustry, getAuthHeaders, onOpenTable, onSchemaUpdated, canManageMilestones }) {
  const [overview, setOverview] = useState(null);
  const [operationalFinance, setOperationalFinance] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isImproving, setIsImproving] = useState(false);
  const [showAddTable, setShowAddTable] = useState(false);
  const [selectedAddTemplateId, setSelectedAddTemplateId] = useState('');
  const [selectedTemplateTable, setSelectedTemplateTable] = useState(null);
  const [error, setError] = useState('');
  const [showAnalysisPanel, setShowAnalysisPanel] = useState(false);
  const [analysisQuestion, setAnalysisQuestion] = useState('');
  const [analysisAnswer, setAnalysisAnswer] = useState('');
  const [analysisError, setAnalysisError] = useState('');
  const [isAnalysing, setIsAnalysing] = useState(false);
  const [milestoneType, setMilestoneType] = useState('Monthly');
  const [milestoneRevenue, setMilestoneRevenue] = useState('');
  const [isSavingMilestone, setIsSavingMilestone] = useState(false);
  const [milestoneMessage, setMilestoneMessage] = useState('');
  const [analysisStartDate, setAnalysisStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().slice(0, 10);
  });
  const [analysisEndDate, setAnalysisEndDate] = useState(() => new Date().toISOString().slice(0, 10));

  const loadOverview = async () => {
    setIsLoading(true);
    setError('');
    try {
      const [res, projectsResponse, customersResponse, staffResponse, milestonesResponse] = await Promise.all([
        fetch(`${MONGO_API_BASE}/api/data-layer/dashboard/overview`, { credentials: 'include', headers: getAuthHeaders() }),
        fetch('https://api.insightos.co.uk/api/getproject', { credentials: 'include' }),
        fetch('https://api.insightos.co.uk/api/customers', { credentials: 'include' }),
        fetch('https://api.insightos.co.uk/api/staff', { credentials: 'include' }),
        fetch('https://api.insightos.co.uk/api/milestones', { credentials: 'include' }),
      ]);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Failed to load dashboard');
      setOverview(data.data);
      const [projectsData, customersData, staffData, milestonesData] = await Promise.all([
        projectsResponse.ok ? projectsResponse.json() : { data: [] },
        customersResponse.ok ? customersResponse.json() : { customers: [] },
        staffResponse.ok ? staffResponse.json() : { data: [] },
        milestonesResponse.ok ? milestonesResponse.json() : { data: [] },
      ]);
      setOperationalFinance(buildOperationalFinance({
        projects: Array.isArray(projectsData?.data) ? projectsData.data : [],
        customers: Array.isArray(customersData?.customers) ? customersData.customers : [],
        staffProfiles: Array.isArray(staffData?.data) ? staffData.data : [],
        milestones: Array.isArray(milestonesData?.data) ? milestonesData.data : [],
        dataLayerSeries: data.data?.timeSeries?.series || [],
      }));
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadOverview();
  }, []);

  const improveModel = async () => {
    if (!activeSchema?.tables?.length) return;
    setIsImproving(true);
    setError('');
    try {
      const planResponse = await fetch(`${MONGO_API_BASE}/api/data-layer/schema/plan`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ industry: companyIndustry || '', draft: { source: 'dashboard-improvement', tables: activeSchema.tables, dashboard: activeSchema.dashboard } }),
      });
      const plan = await planResponse.json();
      if (!planResponse.ok) throw new Error(plan?.message || 'Failed to improve dashboard model');
      const saveResponse = await fetch(`${MONGO_API_BASE}/api/data-layer/schema`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(plan.data),
      });
      const saved = await saveResponse.json();
      if (!saveResponse.ok) throw new Error(saved?.message || 'Failed to save improved dashboard model');
      onSchemaUpdated(saved.data.active);
      await loadOverview();
    } catch (improveError) {
      setError(improveError.message);
    } finally {
      setIsImproving(false);
    }
  };

  const saveMilestone = async (event) => {
    event.preventDefault();
    const revenue = Number(milestoneRevenue);
    if (!Number.isFinite(revenue) || revenue < 0) {
      setMilestoneMessage('Enter a valid revenue target.');
      return;
    }

    setIsSavingMilestone(true);
    setMilestoneMessage('');
    try {
      const response = await fetch('https://api.insightos.co.uk/api/milestones', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ type: milestoneType, revenue }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success) throw new Error(data?.message || 'Failed to save milestone');
      setMilestoneRevenue('');
      setMilestoneMessage(`${milestoneType} milestone saved.`);
      await loadOverview();
    } catch (saveError) {
      setMilestoneMessage(saveError.message || 'Failed to save milestone');
    } finally {
      setIsSavingMilestone(false);
    }
  };

  const analyseData = async (event) => {
    event.preventDefault();
    if (!analysisQuestion.trim()) return;
    setIsAnalysing(true);
    setAnalysisError('');
    try {
      const res = await fetch(`${MONGO_API_BASE}/api/data-layer/dashboard/analyse`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ question: analysisQuestion, startDate: analysisStartDate, endDate: analysisEndDate, operationalFinance }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Unable to analyse the selected data');
      setAnalysisAnswer(data?.data?.answer || 'No analysis was returned.');
    } catch (analysisRequestError) {
      setAnalysisError(analysisRequestError.message);
    } finally {
      setIsAnalysing(false);
    }
  };

  const addTemplateTable = async () => {
    if (!selectedTemplateTable) return;
    setIsImproving(true);
    setError('');
    try {
      const tableNames = [...activeSchema.tables.map((table) => table.name), selectedTemplateTable.name];
      const draft = {
        source: 'dashboard-improvement',
        tables: [...activeSchema.tables, {
          name: selectedTemplateTable.name,
          fields: selectedTemplateTable.fields.map((fieldName) => ({
            name: formatFieldLabel(fieldName),
            ...inferFieldMetadata(fieldName),
            dashboard: true,
            referenceTableKey: inferReferenceTableKey(fieldName, selectedTemplateTable.name, tableNames),
          })),
        }],
        dashboard: activeSchema.dashboard,
      };
      const planResponse = await fetch(`${MONGO_API_BASE}/api/data-layer/schema/plan`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ industry: companyIndustry || '', draft }),
      });
      const plan = await planResponse.json();
      if (!planResponse.ok) throw new Error(plan?.message || 'Failed to plan the new table');
      const saveResponse = await fetch(`${MONGO_API_BASE}/api/data-layer/schema`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify(plan.data),
      });
      const saved = await saveResponse.json();
      if (!saveResponse.ok) throw new Error(saved?.message || 'Failed to save the new table');
      onSchemaUpdated(saved.data.active);
      setSelectedTemplateTable(null);
      setShowAddTable(false);
      await loadOverview();
    } catch (addError) {
      setError(addError.message);
    } finally {
      setIsImproving(false);
    }
  };

  const formatMetric = (metric) => {
    if (metric.aggregation === 'count' || metric.format === 'number') return Number(metric.total || 0).toLocaleString();
    if (metric.format === 'percentage') return new Intl.NumberFormat('en-GB', { style: 'percent', maximumFractionDigits: 1 }).format(metric.total || 0);
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(metric.total || 0);
  };

  const downloadFinancialData = () => {
    const csvValue = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const exportedAt = new Date().toISOString();
    const metricRows = (overview?.metrics || []).map((metric) => [
      exportedAt,
      'Dashboard metric',
      metric.label || metric.metricKey,
      metric.kind || '',
      metric.aggregation || '',
      metric.total ?? 0,
      metric.scheduledTotal ?? '',
    ]);
    const seriesRows = (overview?.timeSeries?.series || []).flatMap((series) => (
      (series.points || []).map((point) => [
        point.date || '',
        'Monthly financial trend',
        series.label || series.key,
        overview?.timeSeries?.kind || '',
        'monthly_total',
        point.value ?? 0,
        '',
      ])
    ));
    const rows = [
      ['Report date', 'Source', 'Metric', 'Financial direction', 'Aggregation', 'Amount (GBP)', 'Scheduled amount (GBP)'],
      ...metricRows,
      ...seriesRows,
    ];
    const file = new Blob([rows.map((row) => row.map(csvValue).join(',')).join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = `data-layer-financials-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  const operationalSeries = operationalFinance?.series || [];
  const chartSeries = [
    ...(operationalSeries.length ? [
      { key: 'total_revenue', label: 'Total revenue', color: '#22c55e', points: operationalSeries.map((point) => ({ date: point.date, value: point.totalRevenue })) },
      { key: 'staff_cost', label: 'Staff cost', color: '#f97316', points: operationalSeries.map((point) => ({ date: point.date, value: point.staffCost })) },
      { key: 'total_costs', label: 'Total costs', color: '#ef4444', points: operationalSeries.map((point) => ({ date: point.date, value: point.totalCosts })) },
      { key: 'net_profit', label: 'Net profit', color: '#a78bfa', points: operationalSeries.map((point) => ({ date: point.date, value: point.netProfit })) },
      { key: 'monthly_target', label: 'Monthly milestone', color: '#38bdf8', points: operationalSeries.map((point) => ({ date: point.date, value: point.monthlyTarget })) },
    ] : overview?.timeSeries?.series || []),
  ];
  const chartKind = operationalSeries.length ? 'currency' : overview?.timeSeries?.kind;
  const selectedTemplate = TEMPLATES.find((template) => template.id === selectedAddTemplateId) || getTemplateForIndustry(companyIndustry) || TEMPLATES[0];
  const existingTableKeys = new Set(activeSchema?.tables?.map((table) => table.tableKey) || []);

  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-400">Data Layer</p>
          <h1 className="mt-1 text-2xl font-semibold text-white">{overview?.dashboard?.title || 'Business overview'}</h1>
          <p className="mt-1 text-sm text-gray-400">A complete view of every table in your data model.</p>
        </div>
        
        <div className="flex gap-2">
          <button type="button" onClick={() => setShowAnalysisPanel(true)} disabled={isLoading} className="inline-flex items-center gap-2 rounded-md border border-cyan-700 bg-cyan-700/20 px-3 py-2 text-sm font-semibold text-cyan-100 hover:bg-cyan-700/30 disabled:opacity-50">
            <SparklesIcon className="size-4" /> Ask about data
          </button>
          <button type="button" onClick={() => { setSelectedAddTemplateId(getTemplateForIndustry(companyIndustry)?.id || ''); setSelectedTemplateTable(null); setShowAddTable(true); }} disabled={isImproving || isLoading} className="inline-flex items-center gap-2 rounded-md border border-emerald-700 bg-emerald-700/20 px-3 py-2 text-sm font-semibold text-emerald-200 hover:bg-emerald-700/30 disabled:opacity-50">
            <PlusIcon className="size-4" /> Add table
          </button>
          <button type="button" onClick={improveModel} disabled={isImproving || isLoading} className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50">
            <ArrowUpCircleIcon className={`size-4 ${isImproving ? 'animate-spin' : ''}`} /> {isImproving ? 'Improving...' : 'Improve model'}
          </button>
          <button type="button" onClick={downloadFinancialData} disabled={isLoading || !overview} className="inline-flex items-center gap-2 rounded-md border border-emerald-700 bg-emerald-700/20 px-3 py-2 text-sm font-semibold text-emerald-100 hover:bg-emerald-700/30 disabled:opacity-50">
            <DocumentArrowDownIcon className="size-4" /> Download financials
          </button>
          <button type="button" onClick={loadOverview} disabled={isLoading || isImproving} className="inline-flex items-center gap-2 rounded-md border border-indigo-800 bg-gray-800/60 px-3 py-2 text-sm font-semibold text-indigo-300 hover:bg-gray-700 disabled:opacity-50">
            <ArrowPathIcon className={`size-4 ${isLoading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {error && <p className="text-sm font-medium text-red-400">{error}</p>}
      {showAddTable && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="add-table-title">
          <button type="button" aria-label="Close add table form" className="fixed inset-0 bg-gray-950/80" onClick={() => setShowAddTable(false)} />
          <div className="relative w-full max-w-4xl rounded-lg bg-white p-6 text-left shadow-2xl">
            <h2 id="add-table-title" className="text-lg font-semibold text-gray-900">Add a template table</h2>
            <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">{TEMPLATES.map((template) => <button key={template.id} type="button" onClick={() => { setSelectedAddTemplateId(template.id); setSelectedTemplateTable(null); }} className={`rounded-md border px-3 py-2 text-left text-sm font-semibold ${selectedTemplate.id === template.id ? 'border-indigo-600 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}>{template.name}</button>)}</div>
            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">{selectedTemplate.tables.map((table) => { const exists = existingTableKeys.has(toSchemaKey(table.name)); const selected = selectedTemplateTable?.name === table.name; return <button key={table.name} type="button" disabled={exists} onClick={() => setSelectedTemplateTable(table)} className={`rounded-md border p-4 text-left ${exists ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400' : selected ? 'border-emerald-600 bg-emerald-50 text-emerald-800' : 'border-gray-200 text-gray-800 hover:border-emerald-400 hover:bg-emerald-50/40'}`}><span className="block font-semibold">{table.name}</span><span className="mt-1 block text-xs">{exists ? 'Already in your model' : `${table.fields.length} fields`}</span></button>; })}</div>
            <div className="mt-6 flex justify-end gap-3"><button type="button" onClick={() => setShowAddTable(false)} className="rounded-md px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100">Cancel</button><button type="button" onClick={addTemplateTable} disabled={!selectedTemplateTable || isImproving} className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50">{isImproving ? 'Adding...' : 'Add selected table'}</button></div>
          </div>
        </div>
      )}
      {isLoading && !overview ? (
        <div className="rounded-xl border border-white/10 bg-gray-900/80 px-6 py-16 text-center text-sm text-gray-400">Loading dashboard...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {[...(overview?.metrics || []), ...(operationalFinance?.metrics || [])].map((metric) => (
              <div key={metric.metricKey} className={`rounded-lg border px-5 py-5 shadow-sm ${metric.metricKey === 'reported_expenditure' || metric.kind === 'expense' ? 'border-red-800/60 bg-red-950/30' : metric.metricKey === 'reported_budget' || metric.kind === 'target' ? 'border-amber-800/60 bg-amber-950/30' : 'border-emerald-800/60 bg-emerald-950/30'}`}>
                <p className={`truncate text-sm font-medium ${metric.metricKey === 'reported_expenditure' || metric.kind === 'expense' ? 'text-red-300' : metric.metricKey === 'reported_budget' || metric.kind === 'target' ? 'text-amber-300' : 'text-emerald-300'}`}>{metric.label}</p>
                <p className={`mt-2 text-3xl font-semibold ${metric.metricKey === 'reported_expenditure' ? 'text-red-100' : metric.metricKey === 'reported_budget' ? 'text-amber-100' : 'text-emerald-100'}`}>{formatMetric(metric)}</p>
                {metric.aggregation === 'monthly_recurring_revenue' && <p className="mt-1 text-xs text-emerald-300">Scheduled: {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(metric.scheduledTotal || 0)}</p>}
              </div>
            ))}
          </div>

          {canManageMilestones && (
            <section className="rounded-lg border border-amber-800/60 bg-amber-950/30 p-5 shadow-sm">
              <h2 className="text-base font-semibold text-amber-100">Set revenue milestone</h2>
              <p className="mt-1 text-sm text-amber-200/80">Set the active weekly, monthly, quarterly, or yearly revenue target.</p>
              <form onSubmit={saveMilestone} className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
                <label className="text-sm font-medium text-amber-100">Period
                  <select value={milestoneType} onChange={(event) => setMilestoneType(event.target.value)} className="mt-1.5 block w-full rounded-md border-amber-800 bg-gray-900 px-3 py-2 text-sm text-white focus:border-amber-500 focus:ring-amber-500">
                    <option value="Weekly">Weekly</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Quarterly">Quarterly</option>
                    <option value="Yearly">Yearly</option>
                  </select>
                </label>
                <label className="text-sm font-medium text-amber-100">Revenue target (GBP)
                  <input required min="0" step="0.01" type="number" value={milestoneRevenue} onChange={(event) => setMilestoneRevenue(event.target.value)} placeholder="e.g. 25000" className="mt-1.5 block w-full rounded-md border-amber-800 bg-gray-900 px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:border-amber-500 focus:ring-amber-500" />
                </label>
                <button disabled={isSavingMilestone} className="self-end rounded-md bg-amber-500 px-4 py-2 text-sm font-semibold text-gray-950 hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-50">{isSavingMilestone ? 'Saving...' : 'Save milestone'}</button>
              </form>
              {milestoneMessage && <p className="mt-3 text-sm font-medium text-amber-200">{milestoneMessage}</p>}
            </section>
          )}

          {(chartSeries.length > 0 || overview?.timeSeries?.points?.length > 0) && (
            <section className="overflow-hidden rounded-lg border border-cyan-800/60 bg-cyan-950/20 px-5 py-5 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                <h2 className="text-base font-semibold text-cyan-100">{operationalSeries.length ? 'Operational finance trend' : overview.timeSeries.label}</h2>
                <p className="mt-1 text-sm text-cyan-200/80">{operationalSeries.length ? 'Combined project, subscription, and Data Layer revenue less staff and Data Layer costs, with the monthly milestone target.' : overview.timeSeries.series?.length ? 'Monthly totals across all date-bearing revenue and expenditure records.' : overview.timeSeries.kind === 'currency' ? 'Projected active subscriptions, using their start and cancellation dates.' : 'A six-month record trend using the first relevant date field in the model.'}</p>
                </div>
                {chartSeries.length > 0 && <div className="flex flex-wrap gap-3 text-xs font-semibold text-cyan-100">{chartSeries.map((series) => <span key={series.key} className="inline-flex items-center gap-2"><span className="size-2.5 rounded-full" style={{ backgroundColor: series.color }} />{series.label}</span>)}</div>}
              </div>
              <div className="mt-4 h-64 w-full">
                <VictoryChart width={720} height={260} padding={{ top: 20, left: 72, right: 28, bottom: 48 }} theme={VictoryTheme.material} domainPadding={{ x: 18, y: 16 }}>
                  <VictoryAxis style={{ axis: { stroke: '#155e75' }, tickLabels: { fill: '#a5f3fc', fontSize: 10 }, grid: { stroke: 'transparent' } }} />
                  <VictoryAxis dependentAxis tickFormat={(value) => chartKind === 'currency' ? `GBP ${Math.round(value)}` : Math.round(value)} style={{ axis: { stroke: '#155e75' }, tickLabels: { fill: '#a5f3fc', fontSize: 10 }, grid: { stroke: '#164e63' } }} />
                  {chartSeries.length ? chartSeries.map((series) => <VictoryLine key={series.key} data={series.points} x="date" y="value" interpolation="monotoneX" style={{ data: { stroke: series.color, strokeWidth: 3 } }} />) : <VictoryLine data={overview.timeSeries.points} x="date" y="value" interpolation="monotoneX" style={{ data: { stroke: '#22d3ee', strokeWidth: 3 } }} />}
                </VictoryChart>
              </div>
            </section>
          )}

          <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
            {(overview?.tables || []).map((table) => (
              <section key={table.tableKey} className="overflow-hidden rounded-xl border border-white/10 bg-gray-900/80 shadow-xl shadow-black/20">
                <div className="flex items-center justify-between gap-4 border-b border-white/10 px-5 py-4">
                  <div>
                    <h2 className="text-base font-semibold text-white">{table.tableName}</h2>
                    <p className="mt-1 text-xs text-gray-400">{table.recordCount.toLocaleString()} record{table.recordCount === 1 ? '' : 's'}</p>
                  </div>
                  <button type="button" onClick={() => onOpenTable(table.tableKey)} className="text-sm font-semibold text-indigo-400 hover:text-indigo-300">Open table</button>
                </div>
                {table.recentRecords.length === 0 ? (
                  <p className="px-5 py-8 text-sm text-gray-400">No records yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-left">
                      <thead className="bg-gray-800/50"><tr>{table.fields.slice(0, 3).map((field) => <th key={field.fieldKey} className="whitespace-nowrap px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-400">{formatFieldLabel(field.name)}</th>)}</tr></thead>
                      <tbody className="divide-y divide-white/5">{table.recentRecords.map((record) => <tr key={record.recordId}>{table.fields.slice(0, 3).map((field) => <td key={field.fieldKey} className="max-w-[180px] truncate whitespace-nowrap px-5 py-3 text-sm text-gray-200">{String(record.values?.[field.fieldKey] ?? '-')}</td>)}</tr>)}</tbody>
                    </table>
                  </div>
                )}
              </section>
            ))}
          </div>
        </>
      )}
      <div className={`fixed inset-0 z-50 ${showAnalysisPanel ? 'pointer-events-auto' : 'pointer-events-none'}`} aria-hidden={!showAnalysisPanel}>
        <button type="button" aria-label="Close data analysis" onClick={() => setShowAnalysisPanel(false)} className={`absolute inset-0 bg-black/60 transition-opacity ${showAnalysisPanel ? 'opacity-100' : 'opacity-0'}`} tabIndex={showAnalysisPanel ? 0 : -1} />
        <aside className={`absolute inset-y-0 right-0 flex w-full max-w-xl flex-col border-l border-cyan-800/60 bg-gray-950 shadow-2xl transition-transform duration-300 ${showAnalysisPanel ? 'translate-x-0' : 'translate-x-full'}`} role="dialog" aria-modal="true" aria-label="Data analysis">
          <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
            <div><p className="text-xs font-semibold uppercase tracking-wide text-cyan-400">Data analysis</p><h2 className="mt-1 text-lg font-semibold text-white">Ask about your business</h2></div>
            <button type="button" onClick={() => setShowAnalysisPanel(false)} className="inline-flex size-9 items-center justify-center rounded-md text-gray-300 hover:bg-gray-800 hover:text-white" aria-label="Close data analysis"><XMarkIcon className="size-5" /></button>
          </div>
          <form onSubmit={analyseData} className="flex flex-1 flex-col overflow-hidden">
            <div className="grid grid-cols-2 gap-3 border-b border-white/10 px-5 py-4">
              <label className="text-xs font-medium text-gray-300">Start date<input required type="date" value={analysisStartDate} max={analysisEndDate} onChange={(event) => setAnalysisStartDate(event.target.value)} className="mt-1.5 block w-full rounded-md border-gray-700 bg-gray-900 text-sm text-white focus:border-cyan-500 focus:ring-cyan-500" /></label>
              <label className="text-xs font-medium text-gray-300">End date<input required type="date" value={analysisEndDate} min={analysisStartDate} onChange={(event) => setAnalysisEndDate(event.target.value)} className="mt-1.5 block w-full rounded-md border-gray-700 bg-gray-900 text-sm text-white focus:border-cyan-500 focus:ring-cyan-500" /></label>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-5">
              {analysisAnswer ? <div className="whitespace-pre-wrap text-sm leading-6 text-gray-200">{analysisAnswer}</div> : <p className="text-sm leading-6 text-gray-400">Ask for trends, revenue performance, actual spending against budget, or practical next actions.</p>}
              {analysisError && <p className="mt-4 text-sm font-medium text-red-400">{analysisError}</p>}
            </div>
            <div className="border-t border-white/10 p-5"><label className="sr-only" htmlFor="data-analysis-question">Your question</label><textarea id="data-analysis-question" required rows={4} value={analysisQuestion} onChange={(event) => setAnalysisQuestion(event.target.value)} placeholder="What should I focus on in this period?" className="block w-full resize-none rounded-md border-gray-700 bg-gray-900 text-sm text-white placeholder:text-gray-500 focus:border-cyan-500 focus:ring-cyan-500" /><button disabled={isAnalysing || !analysisQuestion.trim()} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-md bg-cyan-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-cyan-500 disabled:cursor-not-allowed disabled:opacity-50"><SparklesIcon className={`size-4 ${isAnalysing ? 'animate-pulse' : ''}`} />{isAnalysing ? 'Analysing...' : 'Analyse data'}</button></div>
          </form>
        </aside>
      </div>
    </section>
  );
}

// ------------------------------------------------------------
// MAIN ONBOARDING WIZARD – redesigned
// ------------------------------------------------------------
export default function OnboardingWizard() {
  const user = useContext(AuthContext);
  const [company, setCompany] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTables, setSelectedTables] = useState([]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [schema, setSchema] = useState([]);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [isTemplateMode, setIsTemplateMode] = useState(false);
  const [activeSchema, setActiveSchema] = useState(null);
  const [selectedDataTable, setSelectedDataTable] = useState(null);
  const [isDataWorkspace, setIsDataWorkspace] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const canManageMilestones = ['admin', 'manager'].includes(String(user?.access || user?.Access || user?.role || user?.Role || '').toLowerCase());

  const getAuthHeaders = () => {
    const token = localStorage.getItem('accessToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const res = await fetch('https://api.insightos.co.uk/api/company', {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        if (!res.ok) throw new Error('Failed to fetch company');
        const data = await res.json();
        setCompany(data.data[0]);
      } catch (error) {
        console.error('Failed to fetch company:', error);
        setCompany({ CompanyName: 'Acme Corp', Industry: 'Technology', Website: 'acme.com' });
      }
    };
    fetchCompany();
  }, []);

  useEffect(() => {
    const fetchActiveSchema = async () => {
      try {
        const res = await fetch(`${MONGO_API_BASE}/api/data-layer/schema`, {
          credentials: 'include',
          headers: getAuthHeaders(),
        });
        const data = await res.json();
        if (res.ok && data?.data?.active) {
          setActiveSchema(data.data.active);
          setSelectedDataTable(data.data.active.tables[0]?.tableKey || null);
        }
      } catch (error) {
        console.error('Failed to load Data Layer schema:', error);
      }
    };
    fetchActiveSchema();
  }, []);

  const handleTemplateSelect = (templateId) => {
    const template = TEMPLATES.find((t) => t.id === templateId);
    setSelectedTemplate(template);
    setSelectedTables(template.tables.map((table) => table.name));
    setShowTemplatePicker(true);
  };

  const toggleTable = (tableName) => {
    setSelectedTables((prev) =>
      prev.includes(tableName) ? prev.filter((t) => t !== tableName) : [...prev, tableName]
    );
  };

  const flattenSchemaTables = (plannedTables) => plannedTables.flatMap((table) =>
    (table.fields || []).map((field) => ({ ...field, table: table.name, confirmed: false }))
  );

  const proceedFromTemplate = async () => {
    if (selectedTables.length === 0) return;
    setIsUploading(true);
    const fields = [];
    selectedTemplate.tables.forEach((table) => {
      if (selectedTables.includes(table.name)) {
        table.fields.forEach((fieldName) => {
          const inferred = inferFieldMetadata(fieldName);
          fields.push({
            name: formatFieldLabel(fieldName),
            type: inferred.type,
            sample: '',
            table: table.name,
            financial: inferred.financial,
            dashboard: inferred.financial,
            referenceTableKey: inferReferenceTableKey(fieldName, table.name, selectedTables),
          });
        });
      }
    });

    setSchema(fields);
    setIsTemplateMode(true);
    setCurrentStep(3);
    setIsUploading(false);
  };

  const goDirectToUpload = () => {
    setIsTemplateMode(false);
    setCurrentStep(2);
  };

  const handleFileUpload = async (file) => {
    setUploadedFile(file);
    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${MONGO_API_BASE}/api/data-layer/import/preview`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const response = await res.json();
      if (!res.ok) throw new Error(response?.message || 'Upload failed');
      const detectedColumns = response.data.columns.map((col) => ({
        ...col,
        table: 'Imported Data',
        confirmed: false,
        financial: false,
      }));
      setSchema(flattenSchemaTables([{ name: 'Imported Data', fields: detectedColumns }]));
      setIsTemplateMode(false);
      setCurrentStep(3);
    } catch (error) {
      console.error('Upload failed:', error);
      alert(error.message || 'Failed to process file.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSaveSuccess = (savedSchema) => {
    if (savedSchema) {
      setActiveSchema(savedSchema);
      setSelectedDataTable(savedSchema.tables[0]?.tableKey || null);
    }
    setCurrentStep(4);
  };

  const getStepStatus = (index) => {
    if (index < currentStep) return 'complete';
    if (index === currentStep) return 'current';
    return 'upcoming';
  };

  const totalSteps = 4;

  return (
    <>
      <ManagerNav />
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-blue-950 to-black py-8 px-4 sm:px-6 lg:px-8">
        <div className={`mx-auto grid max-w-[90rem] gap-5 ${activeSchema?.tables?.length ? sidebarCollapsed ? 'xl:grid-cols-[4.5rem_minmax(0,1fr)]' : 'xl:grid-cols-[17rem_minmax(0,1fr)]' : ''}`}>
          <main className="order-2 min-w-0 xl:order-2">
          {isDataWorkspace && activeSchema?.tables?.length > 0 ? (
            <DataTableWorkspace table={withInferredReferences(activeSchema.tables.find((table) => table.tableKey === selectedDataTable) || activeSchema.tables[0], activeSchema.tables)} getAuthHeaders={getAuthHeaders} sidebarCollapsed={sidebarCollapsed} onToggleSidebar={() => setSidebarCollapsed((collapsed) => !collapsed)} onBack={() => setIsDataWorkspace(false)} onTableDeleted={(savedSchema) => { if (savedSchema?.tables?.length) { setActiveSchema(savedSchema); setSelectedDataTable(savedSchema.tables[0].tableKey); } else { setActiveSchema(null); setSelectedDataTable(null); setIsDataWorkspace(false); setCurrentStep(0); } }} />
          ) : activeSchema?.tables?.length > 0 ? (
            <DataLayerDashboard activeSchema={activeSchema} companyIndustry={company?.Industry} getAuthHeaders={getAuthHeaders} canManageMilestones={canManageMilestones} onSchemaUpdated={(savedSchema) => { setActiveSchema(savedSchema); setSelectedDataTable(savedSchema.tables[0]?.tableKey || null); }} onOpenTable={(tableKey) => { setSelectedDataTable(tableKey); setIsDataWorkspace(true); }} />
          ) : (
            <>
          {/* Company Header – updated */}
          {company && (
            <div className="mb-8 flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-gray-900/80 backdrop-blur-xl border border-white/10 p-6 shadow-xl shadow-black/20">
              <div>
                <h1 className="text-2xl font-bold text-white">
                  Welcome, {company.CompanyName}
                </h1>
                <p className="text-sm text-gray-300">
                  {company.Industry} · {company.Website}
                </p>
              </div>
              <div className="rounded-full bg-indigo-500/10 px-4 py-1.5 text-xs font-medium text-indigo-300 ring-1 ring-indigo-500/30">
                Data Import Wizard
              </div>
            </div>
          )}

          {/* Stepper */}
          <nav aria-label="Progress" className="flex items-center justify-center py-4">
            <p className="text-sm font-medium text-white">
              Step {Math.min(currentStep + 1, totalSteps)} of {totalSteps}
            </p>
            <ol role="list" className="ml-8 flex items-center space-x-5">
              {[0, 1, 2, 3].map((index) => {
                const status = getStepStatus(index);
                return (
                  <li key={index}>
                    {status === 'complete' ? (
                      <span className="block size-2.5 rounded-full bg-indigo-500" />
                    ) : status === 'current' ? (
                      <span className="relative flex items-center justify-center">
                        <span aria-hidden="true" className="absolute flex size-5 p-px">
                          <span className="size-full rounded-full bg-indigo-500/30" />
                        </span>
                        <span className="relative block size-2.5 rounded-full bg-indigo-500" />
                      </span>
                    ) : (
                      <span className="block size-2.5 rounded-full bg-white/15" />
                    )}
                    <span className="sr-only">Step {index + 1}</span>
                  </li>
                );
              })}
            </ol>
          </nav>

          {/* Step 0: Choose Method */}
          {currentStep === 0 && (
            <div className="mt-8">
              <h2 className="text-xl font-semibold text-white text-center mb-8">
                How would you like to start?
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                <div
                  onClick={() => {
                    setShowTemplatePicker(true);
                    setCurrentStep(1);
                  }}
                  className="group relative cursor-pointer rounded-2xl bg-gray-900/80 backdrop-blur-xl border border-white/10 p-8 shadow-xl shadow-black/20 hover:shadow-indigo-500/10 hover:border-indigo-500/50 transition-all hover:scale-[1.02]"
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4 rounded-full bg-indigo-500/10 p-4 text-4xl">📋</div>
                    <h3 className="text-lg font-semibold text-white">Start from a Template</h3>
                    <p className="mt-2 text-sm text-gray-300">
                      Choose an industry‑specific template and pick the tables you need.
                    </p>
                    <span className="mt-4 inline-flex items-center text-sm font-medium text-indigo-400 group-hover:text-indigo-300">
                      Browse templates →
                    </span>
                  </div>
                </div>
                <div
                  onClick={goDirectToUpload}
                  className="group relative cursor-pointer rounded-2xl bg-gray-900/80 backdrop-blur-xl border border-white/10 p-8 shadow-xl shadow-black/20 hover:shadow-emerald-500/10 hover:border-emerald-500/50 transition-all hover:scale-[1.02]"
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4 rounded-full bg-emerald-500/10 p-4 text-4xl">📤</div>
                    <h3 className="text-lg font-semibold text-white">Upload Spreadsheet</h3>
                    <p className="mt-2 text-sm text-gray-300">
                      Upload your own Excel or CSV file and let AI detect the schema.
                    </p>
                    <span className="mt-4 inline-flex items-center text-sm font-medium text-emerald-400 group-hover:text-emerald-300">
                      Upload now →
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Template Selection */}
          {currentStep === 1 && (
            <div className="mt-8 rounded-2xl bg-gray-900/80 backdrop-blur-xl border border-white/10 p-6 shadow-xl shadow-black/20">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white">Select a Template</h3>
                <button
                  onClick={() => {
                    setCurrentStep(0);
                    setShowTemplatePicker(false);
                  }}
                  className="text-sm text-gray-400 hover:text-gray-300"
                >
                  ← Back
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {TEMPLATES.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => handleTemplateSelect(template.id)}
                    className={`cursor-pointer rounded-xl bg-gray-800/60 p-4 transition hover:shadow-md ring-1 ${
                      selectedTemplate?.id === template.id
                        ? 'ring-2 ring-indigo-500'
                        : 'ring-white/10 hover:ring-indigo-400/50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-3xl">{template.icon}</span>
                      <div>
                        <h4 className="font-semibold text-white">{template.name}</h4>
                        <p className="text-xs text-gray-400">{template.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {selectedTemplate && (
                <div className="mt-6 border-t border-white/10 pt-6">
                  <p className="text-sm font-medium text-gray-300 mb-3">
                    Tables in <span className="text-indigo-400">{selectedTemplate.name}</span>
                  </p>
                  <div className="flex flex-wrap gap-4">
                    {selectedTemplate.tables.map((table) => (
                      <label key={table.name} className="flex items-center gap-2 text-sm text-gray-300">
                        <input
                          type="checkbox"
                          checked={selectedTables.includes(table.name)}
                          onChange={() => toggleTable(table.name)}
                          className="rounded border-gray-600 bg-gray-800 text-indigo-500 focus:ring-indigo-500"
                        />
                        {table.name}
                        <span className="text-xs text-gray-500">({table.fields.length} fields)</span>
                      </label>
                    ))}
                  </div>
                  <button
                    onClick={proceedFromTemplate}
                    disabled={selectedTables.length === 0}
                    className={`mt-4 rounded-lg px-6 py-2 text-sm font-semibold text-white transition ${
                      selectedTables.length > 0
                        ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 shadow-lg shadow-indigo-950/40 hover:from-indigo-500 hover:to-indigo-600'
                        : 'bg-gray-700 cursor-not-allowed'
                    }`}
                  >
                    Continue with {selectedTables.length} table{selectedTables.length > 1 ? 's' : ''} →
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Upload */}
          {currentStep === 2 && (
            <div className="mt-8 rounded-2xl border-2 border-dashed border-gray-700 bg-gray-900/80 backdrop-blur-xl p-12 text-center transition hover:border-indigo-500">
              <div className="flex justify-center gap-8 mb-6">
                <div className="flex flex-col items-center gap-2">
                  <div className="flex size-16 items-center justify-center rounded-xl bg-green-950/40 text-2xl font-bold text-green-400 shadow-sm ring-1 ring-green-800/60">
                    📊
                  </div>
                  <span className="text-xs font-medium text-gray-400">Excel</span>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <div className="flex size-16 items-center justify-center rounded-xl bg-blue-950/40 text-2xl font-bold text-blue-400 shadow-sm ring-1 ring-blue-800/60">
                    📝
                  </div>
                  <span className="text-xs font-medium text-gray-400">Google Sheets</span>
                </div>
              </div>
              <p className="text-sm text-gray-300 mb-4">
                Upload a <strong className="text-white">.xlsx</strong>, <strong className="text-white">.xls</strong>, or <strong className="text-white">.csv</strong> file
              </p>
              <div className="flex justify-center">
                <label className="relative cursor-pointer rounded-md bg-gradient-to-r from-indigo-600 to-indigo-700 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-950/40 hover:from-indigo-500 hover:to-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 disabled:opacity-50">
                  <DocumentArrowUpIcon className="-ml-0.5 mr-1.5 inline size-5" />
                  Choose a file
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    className="sr-only"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload(file);
                    }}
                    disabled={isUploading}
                  />
                </label>
              </div>
              {uploadedFile && (
                <p className="mt-4 text-sm text-gray-400">
                  Uploading: {uploadedFile.name}
                  {isUploading && <span className="ml-2 inline-block animate-pulse text-indigo-400">⏳ Processing...</span>}
                </p>
              )}
              <button
                onClick={() => {
                  setCurrentStep(0);
                  setShowTemplatePicker(false);
                }}
                className="mt-6 text-sm text-indigo-400 hover:underline"
              >
                ← Back to method selection
              </button>
            </div>
          )}

          {/* Step 3: Schema Editor */}
          {currentStep === 3 && (
            <SchemaEditor
              initialSchema={schema}
              onSave={handleSaveSuccess}
              onBack={() => {
                if (isTemplateMode) {
                  setCurrentStep(1);
                } else {
                  setCurrentStep(2);
                }
              }}
              companyName={company?.CompanyName}
              companyIndustry={company?.Industry}
              isTemplate={isTemplateMode}
              selectedTables={selectedTables}
            />
          )}

          {/* Step 4: Success */}
          {currentStep === 4 && (
            <div className="mt-12 rounded-2xl bg-gray-900/80 backdrop-blur-xl border border-white/10 p-12 text-center shadow-xl shadow-black/20">
              <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-green-950/40 text-4xl">✅</div>
              <h2 className="mt-4 text-xl font-semibold text-white">Schema Saved Successfully!</h2>
              <p className="mt-2 text-sm text-gray-300">
                Your data model is ready. You can now start adding records or run advanced formulas.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="mt-6 rounded-md bg-gradient-to-r from-indigo-600 to-indigo-700 px-6 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-950/40 hover:from-indigo-500 hover:to-indigo-600"
              >
                Go to Dashboard
              </button>
            </div>
          )}
            </>
          )}
          </main>

          {/* Sidebar – Data Layer table list */}
          {activeSchema?.tables?.length > 0 && (
            <aside className="order-1 h-fit overflow-hidden rounded-xl border border-white/10 bg-gray-900/80 backdrop-blur-xl shadow-xl shadow-black/20 xl:sticky xl:top-6 xl:order-1">
              <div className={`border-b border-white/10 py-4 ${sidebarCollapsed ? 'px-3 text-center' : 'px-5'}`}>
                <button type="button" onClick={() => setSidebarCollapsed((collapsed) => !collapsed)} className="mb-3 inline-flex size-8 items-center justify-center rounded-md bg-gray-800/60 text-gray-300 hover:bg-gray-700" aria-label={sidebarCollapsed ? 'Expand tables sidebar' : 'Collapse tables sidebar'}>
                  {sidebarCollapsed ? <ChevronRightIcon className="size-5" /> : <ChevronLeftIcon className="size-5" />}
                </button>
                {!sidebarCollapsed && <>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">Data Layer</p>
                  <h2 className="mt-1 text-lg font-semibold text-white">Your tables</h2>
                  <p className="mt-1 text-xs text-gray-400">Revision {activeSchema.revision}</p>
                </>}
              </div>
              <nav aria-label="Data Layer tables" className="p-3">
                {activeSchema.tables.map((table) => (
                  <button
                    key={table.tableKey}
                    type="button"
                    onClick={() => { setSelectedDataTable(table.tableKey); setIsDataWorkspace(true); }}
                    className={`group mb-1 flex w-full items-center gap-x-3 rounded-md px-3 py-3 text-left text-sm transition last:mb-0 ${
                      selectedDataTable === table.tableKey
                        ? 'bg-indigo-600/40 text-white shadow-sm'
                        : 'text-gray-300 hover:bg-gray-800/60 hover:text-white'
                    }`}
                  >
                    {selectedDataTable === table.tableKey ? (
                      <ArrowUpCircleIcon className="size-5 shrink-0 text-white" />
                    ) : (
                      <ArrowPathIcon className="size-5 shrink-0 text-gray-500 group-hover:text-white" />
                    )}
                    {!sidebarCollapsed && <span className="min-w-0 flex-1 truncate font-semibold">{table.name}</span>}
                    {!sidebarCollapsed && <span className={`shrink-0 text-xs ${selectedDataTable === table.tableKey ? 'text-gray-200' : 'text-gray-500'}`}>{table.fields.length}</span>}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => { window.location.href = '/customercrm'; }}
                  className="group mt-3 flex w-full items-center gap-x-3 border-t border-white/10 px-3 py-3 text-left text-sm text-cyan-300 transition hover:bg-cyan-950/40 hover:text-cyan-100"
                >
                  <SparklesIcon className="size-5 shrink-0" />
                  {!sidebarCollapsed && <span className="min-w-0 flex-1 truncate font-semibold">Sales / Project CRM</span>}
                </button>
              </nav>
              {!sidebarCollapsed && (
                <div className="border-t border-white/10 px-5 py-4">
                  <p className="text-xs leading-5 text-gray-400">Select a table to open its records, import data, and view financial totals.</p>
                </div>
              )}
            </aside>
          )}
        </div>
      </div>
    </>
  );
}